// client/src/Data.jsx
import { useState, useRef, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import Select from "react-select";
import "./Style.css";
import { BackOfficeHeader } from "./GameSessionHeader";
import {
  formatTransactionCode,
  DEPOSIT_CODES,
  WITHDRAWAL_CODES,
  BOOK_CODES,
  RAKE_CODES,
  TIP_CODES,
  DEALER_PAYOUT_CODES,
  WALLET_INCREASE_CODES,
  WALLET_DECREASE_CODES,
} from "./utils/transactionUtils";

import Papa from "papaparse";
import {
  FaTimes,
  FaSort,
  FaSortUp,
  FaSortDown,
  FaChevronDown,
  FaChevronRight,
  FaInfo,
  FaFileCsv,
} from "react-icons/fa";

export default function Data() {
  // ============================================================================
  // ROUTING & AUTH
  // ============================================================================
  const { cardroom } = useParams();
  const navigate = useNavigate();
  const token = localStorage.getItem("token");
  const permission = localStorage.getItem("permission");
  const dealerMode = Number(localStorage.getItem("dealer_mode") || 0);
  // ============================================================================
  // CORE STATE
  // ============================================================================
  const [players, setPlayers] = useState([]);
  const [currSession, setCurrSession] = useState(null);
  const [openPlayers, setOpenPlayers] = useState({});
  // ============================================================================
  // UI STATE
  // ============================================================================
  const [notifications, setNotifications] = useState([]);

  const [selectedTable, setSelectedTable] = useState(null);
  const [tableDetails, setTableDetails] = useState(null);
  const tableDetailsRef = useRef(null);

  // ============================================================================
  // TRANSACTION HISTORY SEARCH STATE
  // ============================================================================
  const [historySearchType, setHistorySearchType] = useState("player");
  const [historySearchEntity, setHistorySearchEntity] = useState(null);
  const [historyStartTime, setHistoryStartTime] = useState("");
  const [historyEndTime, setHistoryEndTime] = useState("");
  const [historyResults, setHistoryResults] = useState([]);
  const [historyOffset, setHistoryOffset] = useState(0);
  const [hasMoreHistory, setHasMoreHistory] = useState(false);
  const [isHistoryLoading, setIsHistoryLoading] = useState(false);
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [historyTxFilters, setHistoryTxFilters] = useState({
    deposit: true,
    withdrawal: true,
    book: true,
    rake: true,
    tips: true,
    payout: true,
  });

  const [dealers, setDealers] = useState([]);
  const [serviceStaff, setServiceStaff] = useState([]);

  // ============================================================================
  // PLAYER SELECTOR STATE
  // ============================================================================
  const [selectedPlayer, setSelectedPlayer] = useState(null);
  const [playerStats, setPlayerStats] = useState(null);

  const [transactionLimit, setTransactionLimit] = useState(10);
  const [transactionOffset, setTransactionOffset] = useState(0);
  const [hasMoreTransactions, setHasMoreTransactions] = useState(false);

  // ============================================================================
  // ALL PLAYERS TABLE STATE
  // ============================================================================
  const [sortBy, setSortBy] = useState("name");
  const [sortOrder, setSortOrder] = useState("asc");
  const [allPlayersStats, setAllPlayersStats] = useState([]);

  const [selectedTableId, setSelectedTableId] = useState(null);
  const [selectedTableTransactions, setSelectedTableTransactions] = useState(
    [],
  );

  // ============================================================================
  // MODAL STATE - Transaction History
  // ============================================================================
  const [selectedPlayerId, setSelectedPlayerId] = useState(null);
  const [selectedPlayerTransactions, setSelectedPlayerTransactions] = useState(
    [],
  );

  const [allTablesStats, setAllTablesStats] = useState([]);
  const [tablesSortBy, setTablesSortBy] = useState("start_time");
  const [tablesSortOrder, setTablesSortOrder] = useState("desc");

  const [allDealersStats, setAllDealersStats] = useState([]);
  const [dealersSortBy, setDealersSortBy] = useState("name");
  const [dealersSortOrder, setDealersSortOrder] = useState("asc");

  const [allStaffStats, setAllStaffStats] = useState([]);
  const [staffSortBy, setStaffSortBy] = useState("name");
  const [staffSortOrder, setStaffSortOrder] = useState("asc");

  const [txFilters, setTxFilters] = useState({
    rake: true,
    tips: true,
    deposit: true,
    withdrawal: true,
    book: true,
  });

  // ============================================================================
  // FILTER STATE
  // ============================================================================
  const [playerTxFilters, setPlayerTxFilters] = useState({
    debt: true,
    deposit: true,
    withdrawal: true,
  });

  // ============================================================================
  // SESSION STATE
  // ============================================================================
  const [sessions, setSessions] = useState([]);
  const [openSessions, setOpenSessions] = useState({});
  const [sessionTables, setSessionTables] = useState({});
  const [sessionTournaments, setSessionTournaments] = useState({});
  const [sessionTransactions, setSessionTransactions] = useState({});
  const [loadingSessions, setLoadingSessions] = useState(false);
  const [sessionsLoaded, setSessionsLoaded] = useState(false);
  const [activeView, setActiveView] = useState({});
  const [selectedSessionId, setSelectedSessionId] = useState(null);

  const [bookReportChanges, setBookReportChanges] = useState([]);
  const [showBookReportModal, setShowBookReportModal] = useState(false);
  const [bookReportSessionId, setBookReportSessionId] = useState(null);
  const [bookReportPlayerId, setBookReportPlayerId] = useState("");
  const [bookReportTransactions, setBookReportTransactions] = useState(null);

  const [selectedSessionCashId, setSelectedSessionCashId] = useState(null);
  const [sessionCashTransactions, setSessionCashTransactions] = useState({});

  const [showBonusModal, setShowBonusModal] = useState(false);
  const [bonusSessionId, setBonusSessionId] = useState(null);
  const [bonusTransactions, setBonusTransactions] = useState([]);

  const [showWalletReportModal, setShowWalletReportModal] = useState(false);
  const [walletReportSessionId, setWalletReportSessionId] = useState(null);
  const [walletReportWalletId, setWalletReportWalletId] = useState("");
  const [walletReportTransactions, setWalletReportTransactions] =
    useState(null);
  const [walletReportChanges, setWalletReportChanges] = useState([]);
  const [allWallets, setAllWallets] = useState([]);

  const [sessionPayoutFilters, setSessionPayoutFilters] = useState({
    staff: true,
    costs: true,
    wallets: true,
  });

  // ============================================================================
  // EFFECT: Auth & Axios Interceptor
  // ============================================================================
  useEffect(() => {
    if (dealerMode === 2) {
      navigate(`/${cardroom}/dealerdashboard`);
      return;
    } else if (!permission?.includes("V")) {
      navigate("/");
      return;
    }

    const interceptor = axios.interceptors.response.use(
      (response) => response,
      (error) => {
        if (error.response?.status === 401) {
          localStorage.removeItem("token");
          navigate(`/${cardroom}/`);
        }
        return Promise.reject(error);
      },
    );

    return () => axios.interceptors.response.eject(interceptor);
  }, [dealerMode, permission, cardroom, navigate]);

  // ============================================================================
  // EFFECT: Initial Data Load
  // ============================================================================
  useEffect(() => {
    if (!token) {
      navigate(`/`);
      return;
    }

    axios.defaults.headers.common["Authorization"] = `Bearer ${token}`;

    async function loadData() {
      try {
        const { data } = await axios.get(`/${cardroom}/api/cardroom`);
        setCurrSession(data.active);

        const { data: allPlayers } = await axios.get(
          `/${cardroom}/api/players`,
        );
        setPlayers(allPlayers);

        const { data: allDealers } = await axios.get(
          `/${cardroom}/api/dealers`,
        );
        setDealers(allDealers);

        const { data: allStaff } = await axios.get(
          `/${cardroom}/api/service_staff`,
        );
        setServiceStaff(allStaff);

        const { data: wallets } = await axios.get(`/${cardroom}/api/wallets`);
        setAllWallets(wallets);
      } catch (err) {
        console.error("Load data failed", err);
        navigate(`/`);
      }
    }

    loadData();
  }, [token, cardroom, navigate]);

  // ============================================================================
  // EFFECT: Prevent body scroll when modal is open
  // ============================================================================
  useEffect(() => {
    if (
      selectedPlayerId !== null ||
      showBookReportModal ||
      showWalletReportModal ||
      selectedSessionCashId !== null ||
      showBonusModal ||
      selectedTableId !== null
    ) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "unset";
    }

    return () => {
      document.body.style.overflow = "unset";
    };
  }, [
    selectedPlayerId,
    showBookReportModal,
    showWalletReportModal,
    selectedSessionCashId,
    showBonusModal,
    selectedTableId,
  ]);

  // ============================================================================
  // HANDLERS: Load Player Stats
  // ============================================================================
  const loadPlayerStats = async (playerId) => {
    try {
      const { data } = await axios.get(
        `/${cardroom}/api/players/${playerId}/stats`,
      );
      setPlayerStats(data);
    } catch (err) {
      console.error("Failed to load player stats", err);
      showNotification("Failed to load player stats");
    }
  };

  const loadTableTransactions = async (tableId) => {
    try {
      const { data } = await axios.get(
        `/${cardroom}/api/tables/${tableId}/transactions`,
      );
      setSelectedTableTransactions(data);
      setSelectedTableId(tableId);
    } catch (err) {
      console.error("Error loading table transactions:", err);
    }
  };

  const loadAllPlayersStats = async () => {
    try {
      const { data } = await axios.get(`/${cardroom}/api/players/stats/all`);
      setAllPlayersStats(data);
    } catch (err) {
      console.error("Failed to load all players stats", err);
      showNotification("Failed to load player statistics");
    }
  };

  const loadAllTablesStats = async () => {
    try {
      const { data } = await axios.get(`/${cardroom}/api/tables/stats/all`);
      setAllTablesStats(data);
    } catch (err) {
      console.error("Failed to load all tables stats", err);
      showNotification("Failed to load table statistics");
    }
  };

  const loadAllDealersStats = async () => {
    try {
      const { data } = await axios.get(`/${cardroom}/api/dealers/stats/all`);
      setAllDealersStats(data);
    } catch (err) {
      console.error("Failed to load all dealers stats", err);
      showNotification("Failed to load dealer statistics");
    }
  };

  const loadAllStaffStats = async () => {
    try {
      const { data } = await axios.get(
        `/${cardroom}/api/service_staff/stats/all`,
      );
      setAllStaffStats(data);
    } catch (err) {
      console.error("Failed to load all staff stats", err);
      showNotification("Failed to load staff statistics");
    }
  };

  const handleHistorySearch = async (reset = true) => {
    if (!historySearchEntity) return;

    setIsHistoryLoading(true);
    const newOffset = reset ? 0 : historyOffset;

    try {
      let endpoint = "";
      if (historySearchType === "player")
        endpoint = `/${cardroom}/api/history/players/${historySearchEntity.value}/transactions`;
      else if (historySearchType === "dealer")
        endpoint = `/${cardroom}/api/history/dealers/${historySearchEntity.value}/transactions`;
      else if (historySearchType === "staff")
        endpoint = `/${cardroom}/api/history/staff/${historySearchEntity.value}/transactions`;
      else if (historySearchType === "wallet")
        endpoint = `/${cardroom}/api/history/wallets/${historySearchEntity.value}/transactions`;

      const params = {
        limit: 25,
        offset: newOffset,
      };
      if (historyStartTime) params.startTime = historyStartTime;
      if (historyEndTime) params.endTime = historyEndTime;

      const { data } = await axios.get(endpoint, { params });

      if (reset) {
        setHistoryResults(data);
        setShowHistoryModal(true);
      } else {
        setHistoryResults((prev) => [...prev, ...data]);
      }

      setHistoryOffset(newOffset + 25);
      setHasMoreHistory(data.length === 25);
    } catch (err) {
      console.error("History search failed", err);
      showNotification("History search failed");
    } finally {
      setIsHistoryLoading(false);
    }
  };

  const loadTableDetails = async (tableId) => {
    try {
      // For now, just find the table in allTablesStats
      const table = allTablesStats.find((t) => t.id === tableId);
      setTableDetails(table);
    } catch (err) {
      console.error("Failed to load table details", err);
      showNotification("Failed to load table details");
    }
  };
  // ============================================================================
  // HANDLERS: Sorting
  // ============================================================================
  const handleSort = (column) => {
    if (sortBy === column) {
      setSortOrder(sortOrder === "asc" ? "desc" : "asc");
    } else {
      setSortBy(column);
      setSortOrder("asc");
    }
  };

  const togglePlayer = (playerId) => {
    setOpenPlayers((prev) => ({
      ...prev,
      [playerId]: !prev[playerId],
    }));
  };

  const getSortedPlayers = () => {
    const sorted = [...allPlayersStats].sort((a, b) => {
      let aVal = a[sortBy];
      let bVal = b[sortBy];

      // Name sorting
      if (sortBy === "name") {
        aVal = (aVal || "").toLowerCase();
        bVal = (bVal || "").toLowerCase();
        return sortOrder === "asc"
          ? aVal.localeCompare(bVal)
          : bVal.localeCompare(aVal);
      }

      // Handle time string sorting (HH:MM:SS format)
      if (sortBy === "total_playing_time") {
        const timeToSeconds = (timeString) => {
          if (!timeString) return 0;
          const parts = timeString.split(":");
          return (
            parseInt(parts[0]) * 3600 +
            parseInt(parts[1]) * 60 +
            parseInt(parts[2] || 0)
          );
        };
        aVal = timeToSeconds(aVal);
        bVal = timeToSeconds(bVal);
      }

      if (sortBy === "last_played") {
        aVal = aVal ? new Date(aVal).getTime() : 0;
        bVal = bVal ? new Date(bVal).getTime() : 0;
      }

      return sortOrder === "asc" ? aVal - bVal : bVal - aVal;
    });

    return sorted;
  };

  const getSortIcon = (column) => {
    if (sortBy !== column) return <FaSort />;
    return sortOrder === "asc" ? <FaSortUp /> : <FaSortDown />;
  };

  const handleTableSort = (column) => {
    if (tablesSortBy === column) {
      setTablesSortOrder(tablesSortOrder === "asc" ? "desc" : "asc");
    } else {
      setTablesSortBy(column);
      setTablesSortOrder("asc");
    }
  };

  const getSortedTables = () => {
    const sorted = [...allTablesStats].sort((a, b) => {
      let aVal = a[tablesSortBy];
      let bVal = b[tablesSortBy];

      // Handle table name sorting (combined column)
      if (tablesSortBy === "table_name") {
        const getTableDisplay = (table) => {
          const parts = [];
          if (table.name) parts.push(table.name);
          parts.push(table.stakes);
          parts.push(table.game_type);
          if (table.bomb_pots) parts.push("💣");
          return parts.join(" ").toLowerCase();
        };
        aVal = getTableDisplay(a);
        bVal = getTableDisplay(b);
        return tablesSortOrder === "asc"
          ? aVal.localeCompare(bVal)
          : bVal.localeCompare(aVal);
      }

      // Handle time string sorting (HH:MM:SS format)
      if (tablesSortBy === "length") {
        const timeToSeconds = (timeString) => {
          if (!timeString) return 0;
          const parts = timeString.split(":");
          return (
            parseInt(parts[0]) * 3600 +
            parseInt(parts[1]) * 60 +
            parseInt(parts[2] || 0)
          );
        };
        aVal = timeToSeconds(aVal);
        bVal = timeToSeconds(bVal);
      }

      // Handle date sorting
      if (tablesSortBy === "start_time") {
        aVal = aVal ? new Date(aVal).getTime() : 0;
        bVal = bVal ? new Date(bVal).getTime() : 0;
      }

      return tablesSortOrder === "asc" ? aVal - bVal : bVal - aVal;
    });

    return sorted;
  };

  const handleDealerSort = (column) => {
    if (dealersSortBy === column) {
      setDealersSortOrder(dealersSortOrder === "asc" ? "desc" : "asc");
    } else {
      setDealersSortBy(column);
      setDealersSortOrder("asc");
    }
  };

  const getSortedDealers = () => {
    const sorted = [...allDealersStats].sort((a, b) => {
      let aVal = a[dealersSortBy];
      let bVal = b[dealersSortBy];

      if (dealersSortBy === "name") {
        aVal = (aVal || "").toLowerCase();
        bVal = (bVal || "").toLowerCase();
        return dealersSortOrder === "asc"
          ? aVal.localeCompare(bVal)
          : bVal.localeCompare(aVal);
      }

      if (dealersSortBy === "last_dealt") {
        aVal = aVal ? new Date(aVal).getTime() : 0;
        bVal = bVal ? new Date(bVal).getTime() : 0;
      }

      return dealersSortOrder === "asc" ? aVal - bVal : bVal - aVal;
    });
    return sorted;
  };

  const handleStaffSort = (column) => {
    if (staffSortBy === column) {
      setStaffSortOrder(staffSortOrder === "asc" ? "desc" : "asc");
    } else {
      setStaffSortBy(column);
      setStaffSortOrder("asc");
    }
  };

  const getSortedStaff = () => {
    const sorted = [...allStaffStats].sort((a, b) => {
      let aVal = a[staffSortBy];
      let bVal = b[staffSortBy];

      if (staffSortBy === "name") {
        aVal = (aVal || "").toLowerCase();
        bVal = (bVal || "").toLowerCase();
        return staffSortOrder === "asc"
          ? aVal.localeCompare(bVal)
          : bVal.localeCompare(aVal);
      }

      if (staffSortBy === "last_worked") {
        aVal = aVal ? new Date(aVal).getTime() : 0;
        bVal = bVal ? new Date(bVal).getTime() : 0;
      }

      return staffSortOrder === "asc" ? aVal - bVal : bVal - aVal;
    });
    return sorted;
  };

  const getTablesSortIcon = (column) => {
    if (tablesSortBy !== column) return <FaSort />;
    return tablesSortOrder === "asc" ? <FaSortUp /> : <FaSortDown />;
  };

  const formatTableName = (table) => {
    const parts = [];
    if (table.name) parts.push(table.name);
    parts.push(table.stakes);
    parts.push(table.game_type);
    if (table.bomb_pots) parts.push("💣");
    return parts.join(" ");
  };

  // ============================================================================
  // HANDLERS: Notifications
  // ============================================================================
  const showNotification = (message) => {
    const id = Date.now();
    setNotifications((prev) => [...prev, { id, message }]);
    setTimeout(() => {
      setNotifications((prev) => prev.filter((n) => n.id !== id));
    }, 3000);
  };

  // ============================================================================
  // HANDLERS: Select Player
  // ============================================================================
  const handlePlayerSelect = (option) => {
    if (option) {
      setSelectedPlayer(option);
      loadPlayerStats(option.value);
    } else {
      setSelectedPlayer(null);
      setPlayerStats(null);
    }
  };

  // ============================================================================
  // FORMAT HELPERS
  // ============================================================================
  const exportToCSV = (title, headers, data, e) => {
    e.stopPropagation();
    if (!data || data.length === 0) return;
    const csv = Papa.unparse({
      fields: headers,
      data: data,
    });
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute(
      "download",
      `${title}_${new Date().toISOString().split("T")[0]}.csv`,
    );
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const getFilteredHistoryResults = () => {
    return historyResults.filter((tx) => {
      const code = Number(tx.code);
      if (historySearchType === "player") {
        const isDeposit = DEPOSIT_CODES.includes(code) || tx.type === "deposit";
        const isWithdrawal =
          WITHDRAWAL_CODES.includes(code) || tx.type === "withdrawal";
        const isBook = BOOK_CODES.includes(code);

        if (isDeposit && historyTxFilters.deposit) return true;
        if (isWithdrawal && historyTxFilters.withdrawal) return true;
        if (isBook && historyTxFilters.book) return true;

        // fallback
        if (!isDeposit && !isWithdrawal && !isBook) return true;
        return false;
      } else if (historySearchType === "dealer") {
        const isRake = RAKE_CODES.includes(code) || tx.type === "rake";
        const isTip = TIP_CODES.includes(code) || tx.type === "tip";
        const isPayout =
          DEALER_PAYOUT_CODES.includes(code) || tx.type === "payout";

        if (isRake && historyTxFilters.rake) return true;
        if (isTip && historyTxFilters.tips) return true;
        if (isPayout && historyTxFilters.payout) return true;

        if (!isRake && !isTip && !isPayout) return true;
        return false;
      }
      return true;
    });
  };

  const getFilteredTableTransactions = () => {
    return selectedTableTransactions.filter((t) => {
      const tk = String(t.type || "").toLowerCase();
      const code = t.code;

      const isRake = (code && RAKE_CODES.includes(code)) || tk.includes("rake");
      const isTip = (code && TIP_CODES.includes(code)) || tk === "tip";
      const isDeposit =
        (code && DEPOSIT_CODES.includes(code)) || tk === "deposit";
      const isWithdrawal =
        (code && WITHDRAWAL_CODES.includes(code)) || tk === "withdrawal";
      const isBook =
        (code && BOOK_CODES.includes(code)) ||
        tk === "debt payback" ||
        tk === "session credit payback";

      let isMatched = false;
      let show = false;

      if (isRake) {
        isMatched = true;
        if (txFilters.rake) show = true;
      }
      if (isTip) {
        isMatched = true;
        if (txFilters.tips) show = true;
      }
      if (isDeposit) {
        isMatched = true;
        if (txFilters.deposit) show = true;
      }
      if (isWithdrawal) {
        isMatched = true;
        if (txFilters.withdrawal) show = true;
      }
      if (isBook) {
        isMatched = true;
        if (txFilters.book) show = true;
      }

      if (isMatched) return show;
      return true;
    });
  };

  const formatCurrency = (amount) => {
    const num = parseFloat(amount);
    return num >= 0 ? `$${num.toFixed(0)}` : `-$${Math.abs(num).toFixed(0)}`;
  };

  const formatDuration = (timeString) => {
    if (!timeString) return "0h 0m";

    if (typeof timeString === "string" && timeString.includes(":")) {
      const parts = timeString.split(":");
      const hours = parseInt(parts[0], 10);
      const mins = parseInt(parts[1], 10);
      const secs = parseInt(parts[2] || 0, 10);

      if (hours === 0 && mins === 0) return `${secs}s`;
      if (hours === 0) return `${mins}m`;
      return `${hours}h ${mins}m`;
    }

    // Handle number as minutes (from Sessions.jsx formatDuration)
    const totalMinutes = parseInt(timeString, 10);
    if (isNaN(totalMinutes) || totalMinutes === 0) return "0h 0m";
    const hours = Math.floor(totalMinutes / 60);
    const mins = totalMinutes % 60;
    return `${hours}h ${mins}m`;
  };

  const formatTime = (dateString) => {
    if (!dateString) return "";
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return "";
      return date.toLocaleTimeString(undefined, {
        hour: "numeric",
        minute: "2-digit",
      });
    } catch (e) {
      return "";
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "";
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return "";
      return date.toLocaleDateString(undefined, {
        month: "short",
        day: "numeric",
      });
    } catch (e) {
      return "";
    }
  };

  const openPlayerTransactions = async (playerId, offset = 0) => {
    try {
      const { data } = await axios.get(
        `/${cardroom}/api/transactions/player/${playerId}?limit=${transactionLimit}&offset=${offset}`,
      );

      if (offset === 0) {
        setSelectedPlayerTransactions(data);
      } else {
        setSelectedPlayerTransactions((prev) => [...prev, ...data]);
      }

      setTransactionOffset(offset);
      setHasMoreTransactions(data.length === transactionLimit);
      setSelectedPlayerId(playerId);
    } catch (err) {
      console.error("Failed to load transactions", err);
    }
  };

  const loadMoreTransactions = () => {
    const newOffset = transactionOffset + transactionLimit;
    openPlayerTransactions(selectedPlayerId, newOffset);
  };

  // ============================================================================
  // SESSION HANDLERS
  // ============================================================================
  const loadSessions = async () => {
    try {
      setLoadingSessions(true);
      const { data } = await axios.get(`/${cardroom}/api/sessions/data`);
      setSessions(data);
    } catch (err) {
      console.error("Error loading sessions:", err);
      showNotification("Failed to load sessions");
    } finally {
      setLoadingSessions(false);
    }
  };

  const loadBookChanges = async (sessionId) => {
    try {
      const { data } = await axios.get(
        `/${cardroom}/api/sessions/${sessionId}/book_changes`,
      );
      setBookReportChanges(data);
    } catch (err) {
      console.error("Load book changes failed", err);
      setBookReportChanges([]);
    }
  };

  const loadSessionTables = async (sessionId) => {
    try {
      const { data } = await axios.get(
        `/${cardroom}/api/tables/session/${sessionId}`,
      );
      setSessionTables((prev) => ({
        ...prev,
        [sessionId]: data,
      }));
    } catch (err) {
      console.error("Error loading session tables:", err);
      showNotification("Failed to load tables");
    }
  };

  const loadSessionTournaments = async (sessionId) => {
    try {
      const { data } = await axios.get(
        `/${cardroom}/api/sessions/${sessionId}/tournaments`,
      );
      setSessionTournaments((prev) => ({
        ...prev,
        [sessionId]: data,
      }));
    } catch (err) {
      console.error("Error loading session tournaments:", err);
      showNotification("Failed to load tournaments");
    }
  };

  const listSessionCashTransactions = async (sessionId) => {
    setSelectedSessionCashId(sessionId);
    if (sessionCashTransactions[sessionId]) return;

    try {
      const { data } = await axios.get(
        `/${cardroom}/api/sessions/${sessionId}/cashbag_transactions`,
      );
      setSessionCashTransactions((prev) => ({
        ...prev,
        [sessionId]: data,
      }));
    } catch (err) {
      console.error("List session cash transactions failed", err);
    }
  };

  const loadSessionTransactions = async (sessionId) => {
    try {
      const { data } = await axios.get(
        `/${cardroom}/api/sessions/${sessionId}/transactions`,
      );
      setSessionTransactions((prev) => ({
        ...prev,
        [sessionId]: data,
      }));
    } catch (err) {
      console.error("Error loading session transactions:", err);
      showNotification("Failed to load transactions");
    }
  };

  const loadBonusTransactions = async (sessionId) => {
    try {
      const { data } = await axios.get(
        `/${cardroom}/api/sessions/${sessionId}/bonuses`,
      );
      setBonusTransactions(data);
    } catch (err) {
      console.error("Load bonus transactions failed", err);
      setBonusTransactions([]);
    }
  };

  const searchWalletReport = async () => {
    if (!walletReportWalletId) return;

    try {
      const { data } = await axios.get(
        `/${cardroom}/api/sessions/${walletReportSessionId}/wallets/${walletReportWalletId}/transactions`,
      );
      setWalletReportTransactions(data);
    } catch (err) {
      console.error("Search wallet report failed", err);
      setWalletReportTransactions([]);
    }
  };

  const loadWalletChanges = async (sessionId) => {
    try {
      const { data } = await axios.get(
        `/${cardroom}/api/sessions/${sessionId}/wallet_changes`,
      );
      setWalletReportChanges(data);
    } catch (err) {
      console.error("Load wallet changes failed", err);
      setWalletReportChanges([]);
    }
  };

  const toggleSession = (sessionId) => {
    setOpenSessions((prev) => ({
      ...prev,
      [sessionId]: !prev[sessionId],
    }));

    // Reset active view when closing
    if (openSessions[sessionId]) {
      setActiveView((prev) => ({
        ...prev,
        [sessionId]: null,
      }));
    }
  };

  const showTables = (sessionId) => {
    setActiveView((prev) => ({
      ...prev,
      [sessionId]: prev[sessionId] === "tables" ? null : "tables",
    }));

    // Load tables if not already loaded and we're showing them
    if (activeView[sessionId] !== "tables" && !sessionTables[sessionId]) {
      loadSessionTables(sessionId);
    }
  };

  const showTournaments = (sessionId) => {
    setActiveView((prev) => ({
      ...prev,
      [sessionId]: prev[sessionId] === "tournaments" ? null : "tournaments",
    }));

    // Load tournaments if not already loaded and we're showing them
    if (
      activeView[sessionId] !== "tournaments" &&
      !sessionTournaments[sessionId]
    ) {
      loadSessionTournaments(sessionId);
    }
  };

  const showTransactions = (sessionId) => {
    // We'll use a local state or just set the ID for the modal
    setSelectedSessionId(sessionId); // This refers to the session payouts modal

    // Load transactions if not already loaded
    if (!sessionTransactions[sessionId]) {
      loadSessionTransactions(sessionId);
    }
  };

  const searchBookReport = async () => {
    if (!bookReportPlayerId) return;

    try {
      const { data } = await axios.get(
        `/${cardroom}/api/sessions/${bookReportSessionId}/players/${bookReportPlayerId}/book_transactions`,
      );
      setBookReportTransactions(data);
    } catch (err) {
      console.error("Search book report failed", err);
      setBookReportTransactions([]);
    }
  };

  // ============================================================================
  // RENDER: Player Options for Select
  // ============================================================================
  const playerOptions = players.map((p) => ({
    value: p.id,
    label: p.name,
  }));

  // ============================================================================
  // RENDER
  // ============================================================================
  return (
    <div className="game">
      <BackOfficeHeader
        activePage="data"
        cardroom={cardroom}
        centerTitle="Data Analytics"
        dealerMode={dealerMode}
        title="Data Analytics"
      />
{/* Notifications */}
      {notifications.length > 0 && (
        <div className="game-notifications">
          {notifications.map((n) => (
            <div key={n.id} className="game-notification">
              <span className="game-notification__icon">ℹ️</span>
              <span className="game-notification__message">{n.message}</span>
              <button
                className="game-notification__close"
                onClick={() =>
                  setNotifications((prev) =>
                    prev.filter((notif) => notif.id !== n.id),
                  )
                }
              >
                <FaTimes />
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Main Content */}
      <div className="game-container">
        {/* ============================================================================
            NEW SECTION: TRANSACTION HISTORY SEARCH
            ============================================================================ */}
        <div className="game-section">
          <h2
            className="game-section__title"
            style={{ display: "flex", alignItems: "center", gap: "0.75rem" }}
          >
            Transaction History
          </h2>
          <div
            style={{
              display: "flex",
              flexWrap: "wrap",
              gap: "1.25rem",
              marginBottom: "0.5rem",
              alignItems: "end",
            }}
          >
            <div style={{ flex: "0 0 140px" }}>
              <label
                className="game-text-muted"
                style={{
                  display: "block",
                  fontSize: "0.75rem",
                  marginBottom: "0.25rem",
                }}
              >
                TYPE
              </label>
              <select
                className="game-input"
                value={historySearchType}
                onChange={(e) => {
                  setHistorySearchType(e.target.value);
                  setHistorySearchEntity(null);
                  setHistoryResults([]);
                  setHasMoreHistory(false);
                }}
              >
                <option value="player">Player</option>
                <option value="wallet">Wallet</option>
                <option value="dealer">Dealer</option>
                <option value="staff">Staff</option>
              </select>
            </div>

            <div style={{ flex: "1 1 200px", minWidth: "200px" }}>
              <label
                className="game-text-muted"
                style={{
                  display: "block",
                  fontSize: "0.75rem",
                  marginBottom: "0.25rem",
                }}
              >
                SELECT ENTITY
              </label>
              <Select
                options={
                  historySearchType === "player"
                    ? players.map((p) => ({ value: p.id, label: p.name }))
                    : historySearchType === "wallet"
                      ? allWallets.map((w) => ({ value: w.id, label: w.name }))
                      : historySearchType === "dealer"
                        ? dealers.map((d) => ({ value: d.id, label: d.name }))
                        : serviceStaff.map((s) => ({
                            value: s.id,
                            label: s.name,
                          }))
                }
                value={historySearchEntity}
                onChange={setHistorySearchEntity}
                placeholder={`Search ${historySearchType}...`}
                isSearchable
                styles={{
                  control: (base) => ({
                    ...base,
                    backgroundColor: "var(--game-surface)",
                    borderColor: "var(--game-border)",
                    color: "var(--game-text-primary)",
                    minHeight: "2.75rem",
                  }),
                  menu: (base) => ({
                    ...base,
                    backgroundColor: "var(--game-surface)",
                    zIndex: 100,
                  }),
                  option: (base, { isFocused }) => ({
                    ...base,
                    backgroundColor: isFocused
                      ? "var(--game-surface-hover)"
                      : "transparent",
                    color: "var(--game-text-primary)",
                  }),
                  singleValue: (base) => ({
                    ...base,
                    color: "var(--game-text-primary)",
                  }),
                  input: (base) => ({
                    ...base,
                    color: "var(--game-text-primary)",
                  }),
                }}
              />
            </div>

            <div style={{ flex: "0 0 200px" }}>
              <label
                className="game-text-muted"
                style={{
                  display: "block",
                  fontSize: "0.75rem",
                  marginBottom: "0.25rem",
                }}
              >
                START TIME (OPTIONAL)
              </label>
              <input
                type="datetime-local"
                className="game-input"
                value={historyStartTime}
                onChange={(e) => setHistoryStartTime(e.target.value)}
              />
            </div>

            <div style={{ flex: "0 0 200px" }}>
              <label
                className="game-text-muted"
                style={{
                  display: "block",
                  fontSize: "0.75rem",
                  marginBottom: "0.25rem",
                }}
              >
                END TIME (OPTIONAL)
              </label>
              <input
                type="datetime-local"
                className="game-input"
                value={historyEndTime}
                onChange={(e) => setHistoryEndTime(e.target.value)}
              />
            </div>

            <button
              className="game-btn game-btn--primary"
              onClick={() => handleHistorySearch(true)}
              disabled={!historySearchEntity || isHistoryLoading}
              style={{
                padding: "0 1.5rem",
                height: "2.75rem",
                marginBottom: "0",
              }}
            >
              {isHistoryLoading ? "Searching..." : "Search"}
            </button>
          </div>
        </div>

        {/* Player Selector Section */}
        <div className="game-section">
          <h2 className="game-section__title">Player Stats</h2>

          <div
            style={{
              marginBottom: "1.5rem",
              display: "flex",
              alignItems: "center",
              gap: "0.75rem",
            }}
          >
            <Select
              required
              isSearchable
              className="game-select"
              classNamePrefix="select"
              options={playerOptions}
              value={selectedPlayer}
              onChange={handlePlayerSelect}
              placeholder="Select a player..."
              styles={{
                control: (base) => ({
                  ...base,
                  backgroundColor: "#2d3748",
                  borderColor: "#4a5568",
                  minWidth: "250px",
                }),
                menu: (base) => ({
                  ...base,
                  backgroundColor: "#2d3748",
                }),
                option: (base, { isFocused }) => ({
                  ...base,
                  backgroundColor: isFocused ? "#4a5568" : "#2d3748",
                  color: "#e2e8f0",
                }),
                singleValue: (base) => ({
                  ...base,
                  color: "#e2e8f0",
                }),
                input: (base) => ({
                  ...base,
                  color: "#e2e8f0",
                }),
                placeholder: (base) => ({
                  ...base,
                  color: "#718096",
                }),
              }}
            />
            {selectedPlayer && (
              <button
                type="button"
                className="game-icon-btn"
                onClick={() => {
                  setSelectedPlayer(null);
                  setPlayerStats(null);
                }}
              >
                <FaTimes />
              </button>
            )}
          </div>

          {selectedPlayer && playerStats && (
            <>
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
                  gap: "1rem",
                  marginBottom: "1.5rem",
                }}
              >
                <div>
                  <div
                    className="game-text-muted"
                    style={{ fontSize: "0.75rem", marginBottom: "0.25rem" }}
                  >
                    TOTAL DEPOSITS
                  </div>
                  <div style={{ fontSize: "1.125rem", fontWeight: 600 }}>
                    {formatCurrency(playerStats.total_deposits)}
                  </div>
                </div>

                <div>
                  <div
                    className="game-text-muted"
                    style={{ fontSize: "0.75rem", marginBottom: "0.25rem" }}
                  >
                    TOTAL WITHDRAWALS
                  </div>
                  <div style={{ fontSize: "1.125rem", fontWeight: 600 }}>
                    {formatCurrency(playerStats.total_withdrawals)}
                  </div>
                </div>

                <div>
                  <div
                    className="game-text-muted"
                    style={{ fontSize: "0.75rem", marginBottom: "0.25rem" }}
                  >
                    # OF DEPOSITS
                  </div>
                  <div style={{ fontSize: "1.125rem", fontWeight: 600 }}>
                    {playerStats.deposits_count}
                  </div>
                </div>

                <div>
                  <div
                    className="game-text-muted"
                    style={{ fontSize: "0.75rem", marginBottom: "0.25rem" }}
                  >
                    # OF WITHDRAWALS
                  </div>
                  <div style={{ fontSize: "1.125rem", fontWeight: 600 }}>
                    {playerStats.withdrawals_count}
                  </div>
                </div>

                <div>
                  <div
                    className="game-text-muted"
                    style={{ fontSize: "0.75rem", marginBottom: "0.25rem" }}
                  >
                    AVERAGE DEPOSIT
                  </div>
                  <div style={{ fontSize: "1.125rem", fontWeight: 600 }}>
                    {playerStats.deposits_average}
                  </div>
                </div>

                <div>
                  <div
                    className="game-text-muted"
                    style={{ fontSize: "0.75rem", marginBottom: "0.25rem" }}
                  >
                    AVERAGE WITHDRAWAL
                  </div>
                  <div style={{ fontSize: "1.125rem", fontWeight: 600 }}>
                    {playerStats.withdrawals_average}
                  </div>
                </div>

                <div>
                  <div
                    className="game-text-muted"
                    style={{ fontSize: "0.75rem", marginBottom: "0.25rem" }}
                  >
                    NET PROFIT
                  </div>
                  <div
                    style={{
                      fontSize: "1.125rem",
                      fontWeight: 600,
                      color:
                        playerStats.profit > 0
                          ? "var(--game-success)"
                          : playerStats.profit < 0
                            ? "var(--game-danger)"
                            : "",
                    }}
                  >
                    {formatCurrency(playerStats.profit)}
                  </div>
                </div>

                <div>
                  <div
                    className="game-text-muted"
                    style={{ fontSize: "0.75rem", marginBottom: "0.25rem" }}
                  >
                    ACCOUNT BALANCE
                  </div>
                  <div
                    style={{
                      fontSize: "1.125rem",
                      fontWeight: 600,
                      color:
                        playerStats.book < 0
                          ? "var(--game-success)"
                          : playerStats.book > 0
                            ? "var(--game-danger)"
                            : "",
                    }}
                  >
                    {formatCurrency(Math.abs(playerStats.book))}
                  </div>
                </div>

                <div>
                  <div
                    className="game-text-muted"
                    style={{ fontSize: "0.75rem", marginBottom: "0.25rem" }}
                  >
                    SESSION CREDIT
                  </div>
                  <div
                    style={{
                      fontSize: "1.125rem",
                      fontWeight: 600,
                      color:
                        playerStats.session_credit > 0
                          ? "var(--game-danger)"
                          : "",
                    }}
                  >
                    {formatCurrency(playerStats.session_credit)}
                  </div>
                </div>

                <div>
                  <div
                    className="game-text-muted"
                    style={{ fontSize: "0.75rem", marginBottom: "0.25rem" }}
                  >
                    TOTAL PLAYING TIME
                  </div>
                  <div
                    style={{
                      fontSize: "1.125rem",
                      fontWeight: 600,
                      color: "var(--game-text-primary)",
                    }}
                  >
                    {formatDuration(playerStats.total_playing_time)}
                  </div>
                </div>

                <div>
                  <div
                    className="game-text-muted"
                    style={{ fontSize: "0.75rem", marginBottom: "0.25rem" }}
                  >
                    BONUSES
                  </div>
                  <div
                    style={{
                      fontSize: "1.125rem",
                      fontWeight: 600,
                      color: "var(--game-text-primary)",
                    }}
                  >
                    {playerStats.bonuses_count || 0} (
                    {formatCurrency(playerStats.total_bonuses)})
                  </div>
                </div>

                <div>
                  <div
                    className="game-text-muted"
                    style={{ fontSize: "0.75rem", marginBottom: "0.25rem" }}
                  >
                    Sessions Played
                  </div>
                  <div style={{ fontSize: "1.125rem", fontWeight: 600 }}>
                    {playerStats.sessions_count || 0}
                  </div>
                </div>

                <div>
                  <div
                    className="game-text-muted"
                    style={{ fontSize: "0.75rem", marginBottom: "0.25rem" }}
                  >
                    LAST PLAYED
                  </div>
                  <div
                    style={{
                      fontSize: "1.125rem",
                      fontWeight: 600,
                      color: "var(--game-text-primary)",
                    }}
                  >
                    {formatDate(playerStats.last_played)}
                  </div>
                </div>
              </div>
            </>
          )}

          {selectedPlayer && !playerStats && (
            <p className="game-empty">Loading stats...</p>
          )}
        </div>

        {/* All Players Table Section */}
        <div className="game-section">
          <h2
            className="game-section__title"
            style={{
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              gap: "0.5rem",
            }}
            onClick={() => {
              const newState = !openPlayers.allPlayers;
              setOpenPlayers((prev) => ({
                ...prev,
                allPlayers: newState,
              }));
              if (newState && allPlayersStats.length === 0) {
                loadAllPlayersStats();
              }
            }}
          >
            {openPlayers.allPlayers ? <FaChevronDown /> : <FaChevronRight />}{" "}
            All Players
            {openPlayers.allPlayers && (
              <FaFileCsv
                className="game-icon-btn game-icon-btn--success"
                onClick={(e) => {
                  const data = getSortedPlayers().map((p) => ({
                    Name: p.name,
                    Deposits: p.total_deposits,
                    Withdrawals: p.total_withdrawals,
                    Profit: p.profit,
                    "Play Time": formatDuration(p.total_playing_time),
                    Book: p.book,
                    "Sessions Played": p.sessions_count,
                    Bonuses: p.total_bonuses,
                    "Last Played": formatDate(p.last_played),
                  }));
                  exportToCSV(
                    "All_Players",
                    Object.keys(data[0] || {}),
                    data,
                    e,
                  );
                }}
              />
            )}
          </h2>

          {openPlayers.allPlayers && allPlayersStats.length === 0 && (
            <p className="game-empty">Loading players...</p>
          )}

          {openPlayers.allPlayers && allPlayersStats.length > 0 && (
            <div style={{ overflowX: "auto" }}>
              <table
                style={{
                  width: "100%",
                  borderCollapse: "collapse",
                  fontSize: "0.875rem",
                }}
              >
                <thead>
                  <tr style={{ borderBottom: "2px solid var(--game-border)" }}>
                    <th
                      onClick={() => handleSort("name")}
                      style={{
                        padding: "0.75rem",
                        textAlign: "left",
                        fontWeight: "600",
                        color: "var(--game-text-primary)",
                        cursor: "pointer",
                        userSelect: "none",
                        whiteSpace: "nowrap",
                      }}
                    >
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "0.5rem",
                        }}
                      >
                        Name {getSortIcon("name")}
                      </div>
                    </th>
                    <th
                      onClick={() => handleSort("total_deposits")}
                      style={{
                        padding: "0.75rem",
                        textAlign: "left",
                        fontWeight: "600",
                        color: "var(--game-text-primary)",
                        cursor: "pointer",
                        userSelect: "none",
                        whiteSpace: "nowrap",
                      }}
                    >
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "0.5rem",
                        }}
                      >
                        Deposits {getSortIcon("total_deposits")}
                      </div>
                    </th>
                    <th
                      onClick={() => handleSort("total_withdrawals")}
                      style={{
                        padding: "0.75rem",
                        textAlign: "left",
                        fontWeight: "600",
                        color: "var(--game-text-primary)",
                        cursor: "pointer",
                        userSelect: "none",
                        whiteSpace: "nowrap",
                      }}
                    >
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "0.5rem",
                        }}
                      >
                        Withdrawals {getSortIcon("total_withdrawals")}
                      </div>
                    </th>
                    <th
                      onClick={() => handleSort("profit")}
                      style={{
                        padding: "0.75rem",
                        textAlign: "left",
                        fontWeight: "600",
                        color: "var(--game-text-primary)",
                        cursor: "pointer",
                        userSelect: "none",
                        whiteSpace: "nowrap",
                      }}
                    >
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "0.5rem",
                        }}
                      >
                        Profit {getSortIcon("profit")}
                      </div>
                    </th>
                    <th
                      onClick={() => handleSort("total_playing_time")}
                      style={{
                        padding: "0.75rem",
                        textAlign: "left",
                        fontWeight: "600",
                        color: "var(--game-text-primary)",
                        cursor: "pointer",
                        userSelect: "none",
                        whiteSpace: "nowrap",
                      }}
                    >
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "0.5rem",
                        }}
                      >
                        Play Time {getSortIcon("total_playing_time")}
                      </div>
                    </th>
                    <th
                      onClick={() => handleSort("book")}
                      style={{
                        padding: "0.75rem",
                        textAlign: "left",
                        fontWeight: "600",
                        color: "var(--game-text-primary)",
                        cursor: "pointer",
                        userSelect: "none",
                        whiteSpace: "nowrap",
                      }}
                    >
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "0.5rem",
                        }}
                      >
                        Book {getSortIcon("book")}
                      </div>
                    </th>
                    <th
                      onClick={() => handleSort("sessions_count")}
                      style={{
                        padding: "0.75rem",
                        textAlign: "left",
                        fontWeight: "600",
                        color: "var(--game-text-primary)",
                        cursor: "pointer",
                        userSelect: "none",
                        whiteSpace: "nowrap",
                      }}
                    >
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "0.5rem",
                        }}
                      >
                        Sessions Played {getSortIcon("sessions_count")}
                      </div>
                    </th>
                    <th
                      onClick={() => handleSort("total_bonuses")}
                      style={{
                        padding: "0.75rem",
                        textAlign: "left",
                        fontWeight: "600",
                        color: "var(--game-text-primary)",
                        cursor: "pointer",
                        userSelect: "none",
                        whiteSpace: "nowrap",
                      }}
                    >
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "0.5rem",
                        }}
                      >
                        Bonuses {getSortIcon("total_bonuses")}
                      </div>
                    </th>
                    <th
                      onClick={() => handleSort("last_played")}
                      style={{
                        padding: "0.75rem",
                        textAlign: "left",
                        fontWeight: "600",
                        color: "var(--game-text-primary)",
                        cursor: "pointer",
                        userSelect: "none",
                        whiteSpace: "nowrap",
                      }}
                    >
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "0.5rem",
                        }}
                      >
                        Last Played {getSortIcon("last_played")}
                      </div>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {getSortedPlayers().map((player) => (
                    <tr
                      key={player.id}
                      style={{
                        borderBottom: "1px solid var(--game-border)",
                        transition: "background-color 0.2s ease",
                      }}
                      onMouseEnter={(e) =>
                        (e.currentTarget.style.background =
                          "var(--game-surface-hover)")
                      }
                      onMouseLeave={(e) =>
                        (e.currentTarget.style.background = "transparent")
                      }
                    >
                      <td
                        className="game-player-name"
                        style={{
                          padding: "0.75rem",
                          fontWeight: "600",
                          cursor: "pointer",
                        }}
                        onClick={() => {
                          const playerOption = {
                            value: player.id,
                            label: player.name,
                          };
                          setSelectedPlayer(playerOption);
                          loadPlayerStats(player.id);
                          // Scroll to top of page
                          window.scrollTo({ top: 0, behavior: "smooth" });
                        }}
                      >
                        {player.name}
                      </td>

                      <td
                        style={{
                          padding: "0.75rem",
                          fontWeight: "600",
                        }}
                      >
                        {formatCurrency(player.total_deposits)}
                      </td>
                      <td
                        style={{
                          padding: "0.75rem",
                          fontWeight: "600",
                        }}
                      >
                        {formatCurrency(player.total_withdrawals)}
                      </td>
                      <td
                        style={{
                          padding: "0.75rem",
                          color:
                            player.profit > 0
                              ? "var(--game-success)"
                              : player.profit < 0
                                ? "var(--game-danger)"
                                : "",
                          fontWeight: "600",
                        }}
                      >
                        {formatCurrency(player.profit)}
                      </td>
                      <td
                        style={{
                          padding: "0.75rem",
                          color: "var(--game-text-primary)",
                        }}
                      >
                        {formatDuration(player.total_playing_time)}
                      </td>
                      <td
                        style={{
                          padding: "0.75rem",
                          color:
                            player.book < 0
                              ? "var(--game-success)"
                              : player.book > 0
                                ? "var(--game-danger)"
                                : "",

                          fontWeight: "600",
                        }}
                      >
                        {formatCurrency(Math.abs(player.book))}
                      </td>
                      <td
                        style={{
                          padding: "0.75rem",
                          color: "var(--game-text-primary)",
                        }}
                      >
                        {player.sessions_count}
                      </td>
                      <td
                        style={{
                          padding: "0.75rem",
                          color: "var(--game-text-primary)",
                        }}
                      >
                        {formatCurrency(player.total_bonuses || 0)}
                      </td>
                      <td
                        style={{
                          padding: "0.75rem",
                          color: "var(--game-text-secondary)",
                          fontSize: "0.8125rem",
                        }}
                      >
                        {formatDate(player.last_played)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {openPlayers.allPlayers && allPlayersStats.length === 0 && (
            <p className="game-empty">No player data available.</p>
          )}
        </div>

        {/* All Sessions Section */}
        <div className="game-section">
          <h2
            className="game-section__title"
            style={{
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              gap: "0.75rem",
              padding: "0.5rem 0",
              borderBottom: "1px solid var(--game-border)",
              marginBottom: "1.5rem",
            }}
            onClick={() => {
              const newState = !openPlayers.allSessions;
              setOpenPlayers((prev) => ({
                ...prev,
                allSessions: newState,
              }));
              if (newState && sessions.length === 0) {
                loadSessions();
              }
            }}
          >
            {openPlayers.allSessions ? <FaChevronDown /> : <FaChevronRight />}
            All Sessions
            {openPlayers.allSessions && (
              <FaFileCsv
                className="game-icon-btn game-icon-btn--success"
                onClick={(e) => {
                  const data = sessions.map((s) => ({
                    tables: s.tables_count,
                    players: s.players_count,
                    dealers: s.dealers_count,
                    rake: s.rake,
                    "dealer tips": s.dealers_tips,
                    "staff tips": s.service_staff_tips,
                    "deposits count": s.deposits_count,
                    "deposits total": s.deposits_total,
                    "withdrawals count": s.withdrawals_count,
                    "withdrawals total": s.withdrawals_total,
                    "avg deposit": s.deposits_average,
                    "avg withdrawal": s.withdrawals_average,
                    "debt gained": s.debt_gained,
                    "balance gained": s.balance_gained,
                    "ending chips": s.ending_table_chips,
                    "ending cash": s.ending_cash,
                    "max credit": s.max_total_credit,
                    "bonuses count": s.bonuses_count,
                    "bonuses total": s.bonuses_total,
                    costs: s.costs,
                    "cash profit": s.cash_game_profit,
                    "tournament profit": s.tournament_profit,
                    "total profit": s.total_profit,
                  }));
                  exportToCSV(
                    "All_Sessions",
                    Object.keys(data[0] || {}),
                    data,
                    e,
                  );
                }}
              />
            )}
          </h2>

          {openPlayers.allSessions && sessions.length > 0 && (
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                gap: "1rem",
                marginTop: "1rem",
              }}
            >
              {sessions.map((session) => {
                const isOpen = openSessions[session.id];
                return (
                  <div
                    key={session.id}
                    className="game-table-card"
                    style={{
                      border: isOpen
                        ? "2px solid #3182ce"
                        : "1px solid var(--game-border)",
                      borderRadius: "var(--game-radius)",
                      background: "var(--game-surface)",
                      overflow: "hidden",
                      transition: "border-color 0.3s ease",
                      boxShadow: isOpen
                        ? "0 0 10px rgba(49, 130, 206, 0.2)"
                        : "none",
                    }}
                  >
                    <div
                      className="game-table-header"
                      onClick={() => toggleSession(session.id)}
                      style={{
                        cursor: "pointer",
                        padding: "1rem",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        background: isOpen
                          ? "rgba(255, 255, 255, 0.05)"
                          : "transparent",
                        transition: "background 0.2s ease",
                        borderBottom: isOpen
                          ? "1px solid rgba(255, 255, 255, 0.1)"
                          : "none",
                      }}
                    >
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "1.5rem",
                          flex: 1,
                          minWidth: 0,
                        }}
                      >
                        <span
                          className="game-table-name"
                          style={{
                            fontSize: "1rem",
                            fontWeight: 700,
                            whiteSpace: "nowrap",
                          }}
                        >
                          {formatDate(session.start_time)}{" "}
                          {formatTime(session.start_time)}
                        </span>
                        <span
                          className="game-table-stats"
                          style={{
                            color: "var(--game-text-secondary)",
                            fontSize: "0.85rem",
                            whiteSpace: "nowrap",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                          }}
                        >
                          {session.tables_count} tables •{" "}
                          {session.players_count} players •{" "}
                          {session.dealers_count} dealers
                        </span>
                      </div>
                      <div
                        className="game-table-actions"
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "1rem",
                          marginLeft: "1rem",
                        }}
                      >
                        <span
                          className={
                            session.total_profit < 0
                              ? "game-text-danger"
                              : session.total_profit > 0
                                ? "game-text-success"
                                : "game-text-primary"
                          }
                          style={{
                            fontWeight: 700,
                            fontSize: "1.1rem",
                            background: "rgba(0,0,0,0.3)",
                            padding: "0.2rem 0.6rem",
                            borderRadius: "var(--game-radius)",
                            minWidth: "70px",
                            textAlign: "center",
                          }}
                        >
                          {formatCurrency(session.total_profit || 0)}
                        </span>
                        {isOpen ? (
                          <FaChevronDown
                            style={{
                              color: "var(--game-text-secondary)",
                              fontSize: "0.9rem",
                            }}
                          />
                        ) : (
                          <FaChevronRight
                            style={{
                              color: "var(--game-text-secondary)",
                              fontSize: "0.9rem",
                            }}
                          />
                        )}
                      </div>
                    </div>

                    {isOpen && (
                      <div
                        className="game-table-content"
                        style={{
                          padding: "1.5rem",
                          background: "rgba(255, 255, 255, 0.02)",
                          borderTop: "1px solid var(--game-border)",
                        }}
                      >
                        {/* Stats Grid */}
                        <div
                          style={{
                            display: "grid",
                            gridTemplateColumns:
                              "repeat(auto-fit, minmax(180px, 1fr))",
                            gap: "1.5rem",
                            marginBottom: "2rem",
                            padding: "1.5rem",
                            background: "rgba(0,0,0,0.15)",
                            borderRadius: "var(--game-radius)",
                            border: "1px solid rgba(255,255,255,0.05)",
                          }}
                        >
                          {[
                            {
                              label: "RAKE",
                              value: formatCurrency(session.rake || 0),
                            },
                            {
                              label: "DEALER TIPS",
                              value: formatCurrency(session.dealers_tips || 0),
                            },
                            {
                              label: "STAFF TIPS",
                              value: formatCurrency(
                                session.service_staff_tips || 0,
                              ),
                            },
                            {
                              label: "DEPOSITS",
                              value: `${session.deposits_count || 0} (${formatCurrency(session.deposits_total || 0)})`,
                            },
                            {
                              label: "WITHDRAWALS",
                              value: `${session.withdrawals_count || 0} (${formatCurrency(session.withdrawals_total || 0)})`,
                            },
                            {
                              label: "AVG DEPOSIT",
                              value: formatCurrency(
                                session.deposits_average || 0,
                              ),
                            },
                            {
                              label: "AVG WITHDRAWAL",
                              value: formatCurrency(
                                session.withdrawals_average || 0,
                              ),
                            },
                            {
                              label: "DEBT GAINED",
                              value: formatCurrency(session.debt_gained || 0),
                              color:
                                (session.debt_gained || 0) > 0
                                  ? "var(--game-danger)"
                                  : "",
                            },
                            {
                              label: "BALANCE GAINED",
                              value: formatCurrency(
                                session.balance_gained || 0,
                              ),
                              color:
                                (session.balance_gained || 0) > 0
                                  ? "var(--game-success)"
                                  : "",
                            },
                            {
                              label: "ENDING CHIPS",
                              value: formatCurrency(
                                session.ending_table_chips || 0,
                              ),
                              color:
                                (session.ending_table_chips || 0) < 0
                                  ? "var(--game-danger)"
                                  : "",
                            },
                            {
                              label: "ENDING CASH",
                              value: formatCurrency(session.ending_cash || 0),
                            },
                            {
                              label: "MAX CREDIT",
                              value: formatCurrency(
                                session.max_total_credit || 0,
                              ),
                            },
                            {
                              label: "BONUSES",
                              value: `${session.bonuses_count || 0} (${formatCurrency(session.bonuses_total || 0)})`,
                            },
                            {
                              label: "COSTS",
                              value: formatCurrency(session.costs || 0),
                              color:
                                (session.costs || 0) !== 0
                                  ? "var(--game-danger)"
                                  : "",
                            },
                            {
                              label: "CASH PROFIT",
                              value: formatCurrency(
                                session.cash_game_profit || 0,
                              ),
                              color:
                                (session.cash_game_profit || 0) > 0
                                  ? "var(--game-success)"
                                  : (session.cash_game_profit || 0) < 0
                                    ? "var(--game-danger)"
                                    : "",
                            },
                            {
                              label: "TOURNAMENT PROFIT",
                              value: formatCurrency(
                                session.tournament_profit || 0,
                              ),
                              color:
                                (session.tournament_profit || 0) > 0
                                  ? "var(--game-success)"
                                  : (session.tournament_profit || 0) < 0
                                    ? "var(--game-danger)"
                                    : "",
                            },
                            {
                              label: "TOTAL PROFIT",
                              value: formatCurrency(session.total_profit || 0),
                              color:
                                (session.total_profit || 0) > 0
                                  ? "var(--game-success)"
                                  : (session.total_profit || 0) < 0
                                    ? "var(--game-danger)"
                                    : "",
                              bold: true,
                            },
                          ].map((stat, i) => (
                            <div key={i}>
                              <div
                                className="game-text-muted"
                                style={{
                                  fontSize: "0.85rem",
                                  marginBottom: "0.4rem",
                                  fontWeight: 500,
                                  letterSpacing: "0.025em",
                                }}
                              >
                                {stat.label}
                              </div>
                              <div
                                style={{
                                  fontSize: "1.15rem",
                                  fontWeight: stat.bold ? 800 : 700,
                                  color:
                                    stat.color || "var(--game-text-primary)",
                                }}
                              >
                                {stat.value}
                              </div>
                            </div>
                          ))}
                        </div>

                        {/* Action Buttons */}
                        <div
                          style={{
                            display: "flex",
                            gap: "0.5rem",
                            flexWrap: "wrap",
                            marginBottom: "1.5rem",
                          }}
                        >
                          <button
                            className={`game-btn game-btn--sm ${activeView[session.id] === "tables" ? "game-btn--primary" : "game-btn--ghost"}`}
                            onClick={() => showTables(session.id)}
                          >
                            Tables
                          </button>
                          <button
                            className={`game-btn game-btn--sm ${activeView[session.id] === "tournaments" ? "game-btn--primary" : "game-btn--ghost"}`}
                            onClick={() => showTournaments(session.id)}
                          >
                            Tournaments
                          </button>
                          <button
                            className="game-btn game-btn--sm game-btn--ghost"
                            onClick={() => showTransactions(session.id)}
                          >
                            Payouts
                          </button>
                          <button
                            className="game-btn game-btn--sm game-btn--ghost"
                            onClick={() =>
                              listSessionCashTransactions(session.id)
                            }
                          >
                            Cash
                          </button>
                          <button
                            className="game-btn game-btn--sm game-btn--ghost"
                            onClick={() => {
                              setShowBookReportModal(true);
                              setBookReportSessionId(session.id);
                              loadBookChanges(session.id);
                              setBookReportPlayerId("");
                              setBookReportTransactions(null);
                            }}
                          >
                            Book
                          </button>
                          <button
                            className="game-btn game-btn--sm game-btn--ghost"
                            onClick={() => {
                              setShowWalletReportModal(true);
                              setWalletReportSessionId(session.id);
                              loadWalletChanges(session.id);
                            }}
                          >
                            Wallets
                          </button>
                          <button
                            className="game-btn game-btn--sm game-btn--ghost"
                            onClick={() => {
                              setShowBonusModal(true);
                              setBonusSessionId(session.id);
                              loadBonusTransactions(session.id);
                            }}
                          >
                            Bonuses
                          </button>
                        </div>

                        {/* Inline Views (Tables/Tournaments) */}
                        {activeView[session.id] === "tables" && (
                          <div className="game-section-sub">
                            <h4 className="game-section-sub__title">
                              Session Tables
                            </h4>
                            {!sessionTables[session.id] ? (
                              <p className="game-empty">Loading...</p>
                            ) : sessionTables[session.id].length === 0 ? (
                              <p className="game-empty">No tables.</p>
                            ) : (
                              <div style={{ display: "grid", gap: "0.75rem" }}>
                                {sessionTables[session.id].map((t) => (
                                  <div
                                    key={t.id}
                                    className="game-sub-item"
                                    style={{
                                      border: "1px solid var(--game-border)",
                                      borderRadius: "var(--game-radius)",
                                      background: "rgba(0,0,0,0.1)",
                                      marginBottom: "0.75rem",
                                      padding: "1.25rem",
                                    }}
                                  >
                                    <div
                                      style={{
                                        display: "flex",
                                        justifyContent: "space-between",
                                        alignItems: "center",
                                      }}
                                    >
                                      <div style={{ flex: 1 }}>
                                        <div
                                          style={{
                                            display: "flex",
                                            alignItems: "center",
                                            gap: "0.75rem",
                                            marginBottom: "0.5rem",
                                          }}
                                        >
                                          <span
                                            style={{
                                              fontWeight: 700,
                                              fontSize: "1.2rem",
                                              lineHeight: 1.2,
                                            }}
                                          >
                                            {t.name ? `${t.name} - ` : ""}
                                            {t.stakes} {t.game_type}
                                          </span>
                                          <button
                                            className="game-icon-btn game-icon-btn--info"
                                            onClick={() =>
                                              loadTableTransactions(t.id)
                                            }
                                          >
                                            <FaInfo />
                                          </button>
                                        </div>
                                        <div
                                          className="game-text-muted"
                                          style={{
                                            fontSize: "0.9rem",
                                            marginBottom: "0.5rem",
                                          }}
                                        >
                                          {formatTime(t.start_time)} -{" "}
                                          {t.end_time
                                            ? formatTime(t.end_time)
                                            : "Active"}{" "}
                                          • {formatDuration(t.length)}
                                        </div>
                                        <div
                                          className="game-text-muted"
                                          style={{ fontSize: "0.9rem" }}
                                        >
                                          {t.players_count || 0} players
                                          &nbsp;•&nbsp; {t.deposits_count || 0}{" "}
                                          deposits (
                                          {formatCurrency(
                                            t.deposits_total || 0,
                                          )}
                                          ) &nbsp;•&nbsp;{" "}
                                          {t.withdrawals_count || 0} withdrawals
                                          (
                                          {formatCurrency(
                                            t.withdrawals_total || 0,
                                          )}
                                          )
                                        </div>
                                      </div>
                                      <div style={{ textAlign: "right" }}>
                                        <div
                                          className="game-text-success"
                                          style={{
                                            fontWeight: 700,
                                            fontSize: "1.15rem",
                                            marginBottom: "0.25rem",
                                          }}
                                        >
                                          {formatCurrency(t.rake)} rake
                                        </div>
                                        <div
                                          className="game-text-muted"
                                          style={{ fontSize: "1rem" }}
                                        >
                                          {formatCurrency(t.tips)} tips
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        )}

                        {activeView[session.id] === "tournaments" && (
                          <div className="game-section-sub">
                            <h4 className="game-section-sub__title">
                              Session Tournaments
                            </h4>
                            {!sessionTournaments[session.id] ? (
                              <p className="game-empty">Loading...</p>
                            ) : sessionTournaments[session.id].length === 0 ? (
                              <p className="game-empty">No tournaments.</p>
                            ) : (
                              <div style={{ display: "grid", gap: "0.75rem" }}>
                                {sessionTournaments[session.id].map((t) => (
                                  <div
                                    key={t.id}
                                    className="game-sub-item"
                                    style={{
                                      border: "1px solid var(--game-border)",
                                      borderRadius: "var(--game-radius)",
                                      background: "rgba(0,0,0,0.1)",
                                      marginBottom: "0.75rem",
                                      padding: "1.25rem",
                                    }}
                                  >
                                    <div
                                      style={{
                                        display: "flex",
                                        justifyContent: "space-between",
                                        alignItems: "center",
                                      }}
                                    >
                                      <div style={{ flex: 1 }}>
                                        <div
                                          style={{
                                            display: "flex",
                                            alignItems: "center",
                                            gap: "0.75rem",
                                            marginBottom: "0.5rem",
                                          }}
                                        >
                                          <span
                                            style={{
                                              fontWeight: 700,
                                              fontSize: "1.2rem",
                                              lineHeight: 1.2,
                                            }}
                                          >
                                            {t.name}
                                          </span>
                                          <span
                                            className={`game-badge game-badge--${t.state === "finished" ? "secondary" : "success"}`}
                                            style={{ fontSize: "0.75rem" }}
                                          >
                                            {t.state}
                                          </span>
                                        </div>
                                        <div
                                          className="game-text-muted"
                                          style={{ fontSize: "0.95rem" }}
                                        >
                                          {t.total_entries || 0} entries •{" "}
                                          {t.rebuys_num || 0} rebuys •{" "}
                                          {t.addons_num || 0} addons
                                        </div>
                                      </div>
                                      <div style={{ textAlign: "right" }}>
                                        <div
                                          className={
                                            t.profit >= 0
                                              ? "game-text-success"
                                              : "game-text-danger"
                                          }
                                          style={{
                                            fontWeight: 700,
                                            fontSize: "1.15rem",
                                            marginBottom: "0.25rem",
                                          }}
                                        >
                                          {formatCurrency(t.profit)} profit
                                        </div>
                                        <div
                                          className="game-text-muted"
                                          style={{ fontSize: "1rem" }}
                                        >
                                          {formatCurrency(t.total_tips)} tips
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
          {openPlayers.allSessions && sessions.length === 0 && (
            <p className="game-empty">No sessions stored.</p>
          )}
        </div>

        {/* ========== ALL DEALERS ========== */}
        <div className="game-section">
          <h2
            className="game-section__title"
            style={{
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              gap: "0.5rem",
            }}
            onClick={() => {
              const newState = !openPlayers.allDealers;
              togglePlayer("allDealers");
              if (newState && allDealersStats.length === 0) {
                loadAllDealersStats();
              }
            }}
          >
            {openPlayers.allDealers ? <FaChevronDown /> : <FaChevronRight />}
            All Dealers
            {openPlayers.allDealers && (
              <FaFileCsv
                className="game-icon-btn game-icon-btn--success"
                onClick={(e) => {
                  const data = getSortedDealers().map((d) => ({
                    Name: d.name,
                    Tips: d.tips,
                    Owed: d.owed,
                    Sessions: d.sessions_dealt,
                    "Last Dealt": formatDate(d.last_dealt),
                  }));
                  exportToCSV(
                    "All_Dealers",
                    Object.keys(data[0] || {}),
                    data,
                    e,
                  );
                }}
              />
            )}
          </h2>

          {openPlayers.allDealers && allDealersStats.length === 0 && (
            <p className="game-empty">Loading dealers...</p>
          )}

          {openPlayers.allDealers && allDealersStats.length > 0 && (
            <div style={{ overflowX: "auto" }}>
              <table
                style={{
                  width: "100%",
                  borderCollapse: "collapse",
                  fontSize: "0.875rem",
                }}
              >
                <thead>
                  <tr style={{ borderBottom: "2px solid var(--game-border)" }}>
                    <th
                      onClick={() => handleDealerSort("name")}
                      style={{
                        padding: "0.75rem",
                        textAlign: "left",
                        fontWeight: "600",
                        color: "var(--game-text-primary)",
                        cursor: "pointer",
                        userSelect: "none",
                        whiteSpace: "nowrap",
                      }}
                    >
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "0.5rem",
                        }}
                      >
                        Name{" "}
                        {dealersSortBy === "name" ? (
                          dealersSortOrder === "asc" ? (
                            <FaSortUp />
                          ) : (
                            <FaSortDown />
                          )
                        ) : (
                          <FaSort />
                        )}
                      </div>
                    </th>
                    <th
                      onClick={() => handleDealerSort("tips")}
                      style={{
                        padding: "0.75rem",
                        textAlign: "left",
                        fontWeight: "600",
                        color: "var(--game-text-primary)",
                        cursor: "pointer",
                        userSelect: "none",
                        whiteSpace: "nowrap",
                      }}
                    >
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "0.5rem",
                        }}
                      >
                        Tips{" "}
                        {dealersSortBy === "tips" ? (
                          dealersSortOrder === "asc" ? (
                            <FaSortUp />
                          ) : (
                            <FaSortDown />
                          )
                        ) : (
                          <FaSort />
                        )}
                      </div>
                    </th>
                    <th
                      onClick={() => handleDealerSort("owed")}
                      style={{
                        padding: "0.75rem",
                        textAlign: "left",
                        fontWeight: "600",
                        color: "var(--game-text-primary)",
                        cursor: "pointer",
                        userSelect: "none",
                        whiteSpace: "nowrap",
                      }}
                    >
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "0.5rem",
                        }}
                      >
                        Owed{" "}
                        {dealersSortBy === "owed" ? (
                          dealersSortOrder === "asc" ? (
                            <FaSortUp />
                          ) : (
                            <FaSortDown />
                          )
                        ) : (
                          <FaSort />
                        )}
                      </div>
                    </th>
                    <th
                      onClick={() => handleDealerSort("sessions_dealt")}
                      style={{
                        padding: "0.75rem",
                        textAlign: "left",
                        fontWeight: "600",
                        color: "var(--game-text-primary)",
                        cursor: "pointer",
                        userSelect: "none",
                        whiteSpace: "nowrap",
                      }}
                    >
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "0.5rem",
                        }}
                      >
                        Sessions{" "}
                        {dealersSortBy === "sessions_dealt" ? (
                          dealersSortOrder === "asc" ? (
                            <FaSortUp />
                          ) : (
                            <FaSortDown />
                          )
                        ) : (
                          <FaSort />
                        )}
                      </div>
                    </th>
                    <th
                      onClick={() => handleDealerSort("last_dealt")}
                      style={{
                        padding: "0.75rem",
                        textAlign: "left",
                        fontWeight: "600",
                        color: "var(--game-text-primary)",
                        cursor: "pointer",
                        userSelect: "none",
                        whiteSpace: "nowrap",
                      }}
                    >
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "0.5rem",
                        }}
                      >
                        Last Dealt{" "}
                        {dealersSortBy === "last_dealt" ? (
                          dealersSortOrder === "asc" ? (
                            <FaSortUp />
                          ) : (
                            <FaSortDown />
                          )
                        ) : (
                          <FaSort />
                        )}
                      </div>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {getSortedDealers().map((dealer) => (
                    <tr
                      key={dealer.id}
                      style={{
                        borderBottom: "1px solid var(--game-border)",
                        transition: "background-color 0.2s ease",
                      }}
                    >
                      <td
                        style={{
                          padding: "0.75rem",
                          fontWeight: "500",
                          color: "var(--game-text-primary)",
                        }}
                      >
                        {dealer.name}
                      </td>
                      <td style={{ padding: "0.75rem", fontWeight: "600" }}>
                        {formatCurrency(dealer.tips || 0)}
                      </td>
                      <td style={{ padding: "0.75rem", fontWeight: "600" }}>
                        {formatCurrency(dealer.owed || 0)}
                      </td>
                      <td
                        style={{
                          padding: "0.75rem",
                          color: "var(--game-text-primary)",
                        }}
                      >
                        {dealer.sessions_dealt || 0}
                      </td>
                      <td
                        style={{
                          padding: "0.75rem",
                          color: "var(--game-text-secondary)",
                          fontSize: "0.8125rem",
                        }}
                      >
                        {dealer.last_dealt ? (
                          <>
                            <div>
                              {formatTime(dealer.last_dealt)}{" "}
                              {formatDate(dealer.last_dealt)}
                            </div>
                          </>
                        ) : (
                          ""
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {openPlayers.allDealers && allDealersStats.length === 0 && (
            <p className="game-empty">No dealer data available.</p>
          )}
        </div>

        {/* Table Details Section */}
        {selectedTable && tableDetails && (
          <div className="game-section" ref={tableDetailsRef}>
            <h2
              className="game-section__title"
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <span>{formatTableName(tableDetails)}</span>
              <button
                type="button"
                className="game-icon-btn"
                onClick={() => {
                  setSelectedTable(null);
                  setTableDetails(null);
                }}
              >
                <FaTimes />
              </button>
            </h2>

            {/* Rest of the content remains the same... */}

            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
                gap: "1rem",
                marginBottom: "1.5rem",
              }}
            >
              <div>
                <div
                  className="game-text-muted"
                  style={{ fontSize: "0.75rem", marginBottom: "0.25rem" }}
                >
                  TIPS
                </div>
                <div style={{ fontSize: "1.125rem", fontWeight: 600 }}>
                  {formatCurrency(tableDetails.tips || 0)}
                </div>
              </div>

              <div>
                <div
                  className="game-text-muted"
                  style={{ fontSize: "0.75rem", marginBottom: "0.25rem" }}
                >
                  RAKE
                </div>
                <div style={{ fontSize: "1.125rem", fontWeight: 600 }}>
                  {formatCurrency(tableDetails.rake || 0)}
                </div>
              </div>

              <div>
                <div
                  className="game-text-muted"
                  style={{ fontSize: "0.75rem", marginBottom: "0.25rem" }}
                >
                  TOTAL DEPOSITS
                </div>
                <div style={{ fontSize: "1.125rem", fontWeight: 600 }}>
                  {formatCurrency(tableDetails.deposits_total || 0)}
                </div>
              </div>

              <div>
                <div
                  className="game-text-muted"
                  style={{ fontSize: "0.75rem", marginBottom: "0.25rem" }}
                >
                  TOTAL WITHDRAWALS
                </div>
                <div style={{ fontSize: "1.125rem", fontWeight: 600 }}>
                  {formatCurrency(tableDetails.withdrawals_total || 0)}
                </div>
              </div>

              <div>
                <div
                  className="game-text-muted"
                  style={{ fontSize: "0.75rem", marginBottom: "0.25rem" }}
                >
                  # OF DEPOSITS
                </div>
                <div style={{ fontSize: "1.125rem", fontWeight: 600 }}>
                  {tableDetails.deposits_count || 0}
                </div>
              </div>

              <div>
                <div
                  className="game-text-muted"
                  style={{ fontSize: "0.75rem", marginBottom: "0.25rem" }}
                >
                  # OF WITHDRAWALS
                </div>
                <div style={{ fontSize: "1.125rem", fontWeight: 600 }}>
                  {tableDetails.withdrawals_count || 0}
                </div>
              </div>

              <div>
                <div
                  className="game-text-muted"
                  style={{ fontSize: "0.75rem", marginBottom: "0.25rem" }}
                >
                  AVERAGE DEPOSIT
                </div>
                <div style={{ fontSize: "1.125rem", fontWeight: 600 }}>
                  {tableDetails.deposit_average
                    ? formatCurrency(tableDetails.deposit_average)
                    : "$0"}
                </div>
              </div>

              <div>
                <div
                  className="game-text-muted"
                  style={{ fontSize: "0.75rem", marginBottom: "0.25rem" }}
                >
                  AVERAGE WITHDRAWAL
                </div>
                <div style={{ fontSize: "1.125rem", fontWeight: 600 }}>
                  {tableDetails.withdrawal_average
                    ? formatCurrency(tableDetails.withdrawal_average)
                    : "$0"}
                </div>
              </div>

              <div>
                <div
                  className="game-text-muted"
                  style={{ fontSize: "0.75rem", marginBottom: "0.25rem" }}
                >
                  START TIME
                </div>
                <div style={{ fontSize: "1.125rem", fontWeight: 600 }}>
                  {tableDetails.start_time
                    ? new Date(tableDetails.start_time).toLocaleString(
                        undefined,
                        {
                          month: "short",
                          day: "numeric",
                          year: "numeric",
                          hour: "numeric",
                          minute: "2-digit",
                        },
                      )
                    : ""}
                </div>
              </div>

              <div>
                <div
                  className="game-text-muted"
                  style={{ fontSize: "0.75rem", marginBottom: "0.25rem" }}
                >
                  END TIME
                </div>
                <div style={{ fontSize: "1.125rem", fontWeight: 600 }}>
                  {tableDetails.end_time
                    ? new Date(tableDetails.end_time).toLocaleString(
                        undefined,
                        {
                          month: "short",
                          day: "numeric",
                          year: "numeric",
                          hour: "numeric",
                          minute: "2-digit",
                        },
                      )
                    : ""}
                </div>
              </div>

              <div>
                <div
                  className="game-text-muted"
                  style={{ fontSize: "0.75rem", marginBottom: "0.25rem" }}
                >
                  LENGTH
                </div>
                <div style={{ fontSize: "1.125rem", fontWeight: 600 }}>
                  {formatDuration(tableDetails.length)}
                </div>
              </div>

              <div>
                <div
                  className="game-text-muted"
                  style={{ fontSize: "0.75rem", marginBottom: "0.25rem" }}
                >
                  TOTAL PLAYERS
                </div>
                <div style={{ fontSize: "1.125rem", fontWeight: 600 }}>
                  {tableDetails.total_players_count || 0}
                </div>
              </div>

              {tableDetails.notes && (
                <div style={{ gridColumn: "1 / -1" }}>
                  <div
                    className="game-text-muted"
                    style={{ fontSize: "0.75rem", marginBottom: "0.25rem" }}
                  >
                    NOTES
                  </div>
                  <div style={{ fontSize: "1.125rem", fontWeight: 600 }}>
                    {tableDetails.notes}
                  </div>
                </div>
              )}
            </div>

            <button
              className="game-btn game-btn--primary"
              onClick={() => {
                loadTableTransactions(tableDetails.id);
              }}
            >
              View Transactions
            </button>
          </div>
        )}

        {/* ========== ALL TABLES ========== */}
        <div className="game-section">
          <h2
            className="game-section__title"
            style={{
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              gap: "0.5rem",
            }}
            onClick={() => {
              const newState = !openPlayers.allTables;
              togglePlayer("allTables");
              if (newState && allTablesStats.length === 0) {
                loadAllTablesStats();
              }
            }}
          >
            {openPlayers.allTables ? <FaChevronDown /> : <FaChevronRight />}
            All Tables
            {openPlayers.allTables && (
              <FaFileCsv
                className="game-icon-btn game-icon-btn--success"
                onClick={(e) => {
                  const data = getSortedTables().map((t) => ({
                    Table: formatTableName(t),
                    Tips: t.tips,
                    Rake: t.rake,
                    "Deposits Count": t.deposits_count,
                    Deposits: t.deposits_total,
                    "Withdrawals Count": t.withdrawals_count,
                    Withdrawals: t.withdrawals_total,
                    "Start Time": t.start_time
                      ? new Date(t.start_time).toLocaleString()
                      : "",
                    Length: formatDuration(t.length),
                    Players: t.total_players_count,
                  }));
                  exportToCSV(
                    "All_Tables",
                    Object.keys(data[0] || {}),
                    data,
                    e,
                  );
                }}
              />
            )}
          </h2>

          {openPlayers.allTables && allTablesStats.length === 0 && (
            <p className="game-empty">Loading tables...</p>
          )}

          {openPlayers.allTables && allTablesStats.length > 0 && (
            <div style={{ overflowX: "auto" }}>
              <table
                style={{
                  width: "100%",
                  borderCollapse: "collapse",
                  fontSize: "0.875rem",
                }}
              >
                <thead>
                  <tr style={{ borderBottom: "2px solid var(--game-border)" }}>
                    <th
                      onClick={() => handleTableSort("table_name")}
                      style={{
                        padding: "0.75rem",
                        textAlign: "left",
                        fontWeight: "600",
                        color: "var(--game-text-primary)",
                        cursor: "pointer",
                        userSelect: "none",
                        whiteSpace: "nowrap",
                      }}
                    >
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "0.5rem",
                        }}
                      >
                        Table {getTablesSortIcon("table_name")}
                      </div>
                    </th>
                    <th
                      onClick={() => handleTableSort("tips")}
                      style={{
                        padding: "0.75rem",
                        textAlign: "left",
                        fontWeight: "600",
                        color: "var(--game-text-primary)",
                        cursor: "pointer",
                        userSelect: "none",
                        whiteSpace: "nowrap",
                      }}
                    >
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "0.5rem",
                        }}
                      >
                        Tips {getTablesSortIcon("tips")}
                      </div>
                    </th>
                    <th
                      onClick={() => handleTableSort("rake")}
                      style={{
                        padding: "0.75rem",
                        textAlign: "left",
                        fontWeight: "600",
                        color: "var(--game-text-primary)",
                        cursor: "pointer",
                        userSelect: "none",
                        whiteSpace: "nowrap",
                      }}
                    >
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "0.5rem",
                        }}
                      >
                        Rake {getTablesSortIcon("rake")}
                      </div>
                    </th>
                    <th
                      onClick={() => handleTableSort("deposits_total")}
                      style={{
                        padding: "0.75rem",
                        textAlign: "left",
                        fontWeight: "600",
                        color: "var(--game-text-primary)",
                        cursor: "pointer",
                        userSelect: "none",
                        whiteSpace: "nowrap",
                      }}
                    >
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "0.5rem",
                        }}
                      >
                        Deposits {getTablesSortIcon("deposits_total")}
                      </div>
                    </th>
                    <th
                      onClick={() => handleTableSort("withdrawals_total")}
                      style={{
                        padding: "0.75rem",
                        textAlign: "left",
                        fontWeight: "600",
                        color: "var(--game-text-primary)",
                        cursor: "pointer",
                        userSelect: "none",
                        whiteSpace: "nowrap",
                      }}
                    >
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "0.5rem",
                        }}
                      >
                        Withdrawals {getTablesSortIcon("withdrawals_total")}
                      </div>
                    </th>
                    <th
                      onClick={() => handleTableSort("start_time")}
                      style={{
                        padding: "0.75rem",
                        textAlign: "left",
                        fontWeight: "600",
                        color: "var(--game-text-primary)",
                        cursor: "pointer",
                        userSelect: "none",
                        whiteSpace: "nowrap",
                      }}
                    >
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "0.5rem",
                        }}
                      >
                        Start Time {getTablesSortIcon("start_time")}
                      </div>
                    </th>
                    <th
                      onClick={() => handleTableSort("length")}
                      style={{
                        padding: "0.75rem",
                        textAlign: "left",
                        fontWeight: "600",
                        color: "var(--game-text-primary)",
                        cursor: "pointer",
                        userSelect: "none",
                        whiteSpace: "nowrap",
                      }}
                    >
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "0.5rem",
                        }}
                      >
                        Length {getTablesSortIcon("length")}
                      </div>
                    </th>
                    <th
                      onClick={() => handleTableSort("total_players_count")}
                      style={{
                        padding: "0.75rem",
                        textAlign: "left",
                        fontWeight: "600",
                        color: "var(--game-text-primary)",
                        cursor: "pointer",
                        userSelect: "none",
                        whiteSpace: "nowrap",
                      }}
                    >
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "0.5rem",
                        }}
                      >
                        Players {getTablesSortIcon("total_players_count")}
                      </div>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {(() => {
                    let lastSessionId = null;
                    let currentColorIndex = 1;
                    return getSortedTables().map((table) => {
                      const isNewSession = table.session_id !== lastSessionId;
                      if (isNewSession) {
                        currentColorIndex = (currentColorIndex + 1) % 2;
                        lastSessionId = table.session_id;
                      }

                      const sessionBg =
                        currentColorIndex === 1
                          ? "rgba(49, 130, 206, 0.15)"
                          : "rgba(236, 201, 75, 0.1)";

                      return (
                        <tr
                          key={table.id}
                          style={{
                            borderBottom: "1px solid var(--game-border)",
                            transition: "background-color 0.2s ease",
                            background: sessionBg,
                          }}
                          onMouseEnter={(e) =>
                            (e.currentTarget.style.background =
                              "var(--game-surface-hover)")
                          }
                          onMouseLeave={(e) =>
                            (e.currentTarget.style.background = sessionBg)
                          }
                        >
                          <td
                            className="game-player-name"
                            style={{
                              padding: "0.75rem",
                              fontWeight: "500",
                              cursor: "pointer",
                            }}
                            onClick={() => {
                              setSelectedTable(table.id);
                              loadTableDetails(table.id);
                              setTimeout(() => {
                                tableDetailsRef.current?.scrollIntoView({
                                  behavior: "smooth",
                                  block: "start",
                                });
                              }, 100);
                            }}
                          >
                            {formatTableName(table)}
                          </td>
                          <td
                            style={{
                              padding: "0.75rem",
                              fontWeight: "600",
                            }}
                          >
                            {formatCurrency(table.tips || 0)}
                          </td>
                          <td
                            style={{
                              padding: "0.75rem",
                              fontWeight: "600",
                            }}
                          >
                            {formatCurrency(table.rake || 0)}
                          </td>
                          <td
                            style={{
                              padding: "0.75rem",
                              fontWeight: "600",
                            }}
                          >
                            {table.deposits_count} (
                            {formatCurrency(table.deposits_total || 0)})
                          </td>
                          <td
                            style={{
                              padding: "0.75rem",
                              fontWeight: "600",
                            }}
                          >
                            {table.withdrawals_count} (
                            {formatCurrency(table.withdrawals_total || 0)})
                          </td>
                          <td
                            style={{
                              padding: "0.75rem",
                              color: "var(--game-text-secondary)",
                              fontSize: "0.8125rem",
                            }}
                          >
                            {table.start_time
                              ? new Date(table.start_time).toLocaleString(
                                  undefined,
                                  {
                                    month: "short",
                                    day: "numeric",
                                    year: "numeric",
                                    hour: "numeric",
                                    minute: "2-digit",
                                  },
                                )
                              : ""}
                          </td>
                          <td
                            style={{
                              padding: "0.75rem",
                              color: "var(--game-text-secondary)",
                              fontSize: "0.8125rem",
                            }}
                          >
                            {formatDuration(table.length || 0)}
                          </td>
                          <td
                            style={{
                              padding: "0.75rem",
                              color: "var(--game-text-secondary)",
                              fontSize: "0.8125rem",
                            }}
                          >
                            {table.total_players_count || 0}
                          </td>
                        </tr>
                      );
                    });
                  })()}
                </tbody>
              </table>
            </div>
          )}

          {openPlayers.allTables && allTablesStats.length === 0 && (
            <p className="game-empty">No table data available.</p>
          )}
        </div>

        {/* ========== ALL STAFF ========== */}
        <div className="game-section">
          <h2
            className="game-section__title"
            style={{
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              gap: "0.5rem",
            }}
            onClick={() => {
              const newState = !openPlayers.allStaff;
              togglePlayer("allStaff");
              if (newState && allStaffStats.length === 0) {
                loadAllStaffStats();
              }
            }}
          >
            {openPlayers.allStaff ? <FaChevronDown /> : <FaChevronRight />}
            All Staff
            {openPlayers.allStaff && (
              <FaFileCsv
                className="game-icon-btn game-icon-btn--success"
                onClick={(e) => {
                  const data = getSortedStaff().map((s) => ({
                    Name: s.name,
                    Tips: s.tips,
                    Owed: s.owed,
                    "Last Worked": formatDate(s.last_worked),
                  }));
                  exportToCSV("All_Staff", Object.keys(data[0] || {}), data, e);
                }}
              />
            )}
          </h2>

          {openPlayers.allStaff && allStaffStats.length === 0 && (
            <p className="game-empty">Loading staff...</p>
          )}

          {openPlayers.allStaff && allStaffStats.length > 0 && (
            <div style={{ overflowX: "auto" }}>
              <table
                style={{
                  width: "100%",
                  borderCollapse: "collapse",
                  fontSize: "0.875rem",
                }}
              >
                <thead>
                  <tr style={{ borderBottom: "2px solid var(--game-border)" }}>
                    <th
                      onClick={() => handleStaffSort("name")}
                      style={{
                        padding: "0.75rem",
                        textAlign: "left",
                        fontWeight: "600",
                        color: "var(--game-text-primary)",
                        cursor: "pointer",
                        userSelect: "none",
                        whiteSpace: "nowrap",
                      }}
                    >
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "0.5rem",
                        }}
                      >
                        Name{" "}
                        {staffSortBy === "name" ? (
                          staffSortOrder === "asc" ? (
                            <FaSortUp />
                          ) : (
                            <FaSortDown />
                          )
                        ) : (
                          <FaSort />
                        )}
                      </div>
                    </th>
                    <th
                      onClick={() => handleStaffSort("tips")}
                      style={{
                        padding: "0.75rem",
                        textAlign: "left",
                        fontWeight: "600",
                        color: "var(--game-text-primary)",
                        cursor: "pointer",
                        userSelect: "none",
                        whiteSpace: "nowrap",
                      }}
                    >
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "0.5rem",
                        }}
                      >
                        Tips{" "}
                        {staffSortBy === "tips" ? (
                          staffSortOrder === "asc" ? (
                            <FaSortUp />
                          ) : (
                            <FaSortDown />
                          )
                        ) : (
                          <FaSort />
                        )}
                      </div>
                    </th>
                    <th
                      onClick={() => handleStaffSort("owed")}
                      style={{
                        padding: "0.75rem",
                        textAlign: "left",
                        fontWeight: "600",
                        color: "var(--game-text-primary)",
                        cursor: "pointer",
                        userSelect: "none",
                        whiteSpace: "nowrap",
                      }}
                    >
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "0.5rem",
                        }}
                      >
                        Owed{" "}
                        {staffSortBy === "owed" ? (
                          staffSortOrder === "asc" ? (
                            <FaSortUp />
                          ) : (
                            <FaSortDown />
                          )
                        ) : (
                          <FaSort />
                        )}
                      </div>
                    </th>
                    <th
                      onClick={() => handleStaffSort("last_worked")}
                      style={{
                        padding: "0.75rem",
                        textAlign: "left",
                        fontWeight: "600",
                        color: "var(--game-text-primary)",
                        cursor: "pointer",
                        userSelect: "none",
                        whiteSpace: "nowrap",
                      }}
                    >
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "0.5rem",
                        }}
                      >
                        Last Worked{" "}
                        {staffSortBy === "last_worked" ? (
                          staffSortOrder === "asc" ? (
                            <FaSortUp />
                          ) : (
                            <FaSortDown />
                          )
                        ) : (
                          <FaSort />
                        )}
                      </div>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {getSortedStaff().map((member) => (
                    <tr
                      key={member.id}
                      style={{
                        borderBottom: "1px solid var(--game-border)",
                        transition: "background-color 0.2s ease",
                      }}
                    >
                      <td
                        style={{
                          padding: "0.75rem",
                          fontWeight: "500",
                          color: "var(--game-text-primary)",
                        }}
                      >
                        {member.name}
                      </td>
                      <td style={{ padding: "0.75rem", fontWeight: "600" }}>
                        {formatCurrency(member.tips || 0)}
                      </td>
                      <td style={{ padding: "0.75rem", fontWeight: "600" }}>
                        {formatCurrency(member.owed || 0)}
                      </td>
                      <td
                        style={{
                          padding: "0.75rem",
                          color: "var(--game-text-secondary)",
                          fontSize: "0.8125rem",
                        }}
                      >
                        {member.last_worked ? (
                          <>
                            <div>
                              {formatTime(member.last_worked)}{" "}
                              {formatDate(member.last_worked)}
                            </div>
                          </>
                        ) : (
                          ""
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {openPlayers.allStaff && allStaffStats.length === 0 && (
            <p className="game-empty">No staff data available.</p>
          )}
        </div>
      </div>

      {/* Modals */}
      {selectedTableId !== null && (
        <>
          <div
            className="game-modal-overlay"
            onClick={() => {
              setSelectedTableId(null);
              setSelectedTableTransactions([]);
            }}
          />
          <div className="game-modal game-modal--large">
            <div className="game-modal__header">
              <h3
                className="game-modal__title"
                style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}
              >
                Table Transactions -{" "}
                {(() => {
                  if (tableDetails && tableDetails.id === selectedTableId) {
                    return formatTableName(tableDetails);
                  }
                  const tableInStats = allTablesStats.find(
                    (t) => t.id === selectedTableId,
                  );
                  if (tableInStats) return formatTableName(tableInStats);
                  for (const sessionId in sessionTables) {
                    const table = sessionTables[sessionId].find(
                      (t) => t.id === selectedTableId,
                    );
                    if (table) return formatTableName(table);
                  }
                  return `Table ID: ${selectedTableId}`;
                })()}
                {selectedTableTransactions.length > 0 && (
                  <FaFileCsv
                    className="game-icon-btn game-icon-btn--success"
                    onClick={(e) => {
                      const filtered = getFilteredTableTransactions();
                      const data = filtered.map((t) => ({
                        Time: formatTime(t.completed_at),
                        Player: t.player_or_dealer || t.player_name || "",
                        Amount: t.amount,
                        Type: formatTransactionCode(t.code, t.type, t.method),
                        Method: t.method || "",
                        Date: formatDate(t.completed_at),
                      }));
                      exportToCSV(
                        "Table_Transactions",
                        Object.keys(data[0] || {}),
                        data,
                        e,
                      );
                    }}
                  />
                )}
              </h3>
              <button
                className="game-icon-btn"
                onClick={() => {
                  setSelectedTableId(null);
                  setSelectedTableTransactions([]);
                }}
              >
                <FaTimes />
              </button>
            </div>

            <div className="game-modal__filters">
              {[
                { key: "rake", label: "Rake" },
                { key: "tips", label: "Tips" },
                { key: "deposit", label: "Deposits" },
                { key: "withdrawal", label: "Withdrawals" },
                { key: "book", label: "Books" },
              ].map(({ key, label }) => (
                <label key={key} className="game-filter-chip">
                  <input
                    type="checkbox"
                    checked={txFilters[key]}
                    onChange={(e) =>
                      setTxFilters((prev) => ({
                        ...prev,
                        [key]: e.target.checked,
                      }))
                    }
                  />
                  <span>{label}</span>
                </label>
              ))}
            </div>

            <div className="game-modal__body game-modal__body--scroll">
              {(() => {
                const filtered = getFilteredTableTransactions();
                if (filtered.length === 0)
                  return (
                    <p className="game-empty">No matching transactions.</p>
                  );

                return (
                  <div className="game-transaction-list">
                    {filtered.map((t) => {
                      const isNegative =
                        t.type === "withdrawal" ||
                        t.type === "cost" ||
                        t.type?.toLowerCase().includes("payback");
                      const amt = parseFloat(t.amount);
                      return (
                        <div key={t.id} className="game-transaction-item">
                          <span className="game-transaction-time">
                            {formatTime(t.completed_at)}
                          </span>
                          <strong className="game-transaction-player">
                            {t.player_or_dealer || t.player_name || ""}
                          </strong>
                          <span
                            className={`game-transaction-amount ${isNegative ? "game-text-danger" : "game-text-success"}`}
                          >
                            ${Math.abs(amt).toFixed(0)}
                          </span>
                          <span className="game-transaction-type">
                            {formatTransactionCode(t.code, t.type, t.method)}
                          </span>
                          <span className="game-transaction-method">
                            {t.credit
                              ? "credit"
                              : t.method
                                ? `${t.method}${t.receiver_name ? (t.type === "withdrawal" ? " from " + t.receiver_name : " to " + t.receiver_name) : ""}`
                                : ""}
                          </span>
                          <span className="game-transaction-date">
                            {formatDate(t.completed_at)}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                );
              })()}
            </div>
          </div>
        </>
      )}

      {/* Book Report Modal */}
      {showBookReportModal && (
        <>
          <div
            className="game-modal-overlay"
            onClick={() => {
              setShowBookReportModal(false);
              setBookReportSessionId(null);
            }}
          />
          <div className="game-modal game-modal--large">
            <div className="game-modal__header">
              <h3 className="game-modal__title">
                Book Report -{" "}
                {formatDate(
                  sessions.find((s) => s.id === bookReportSessionId)
                    ?.start_time,
                )}
              </h3>
              <button
                className="game-icon-btn"
                onClick={() => {
                  setShowBookReportModal(false);
                  setBookReportSessionId(null);
                }}
              >
                <FaTimes />
              </button>
            </div>

            <div
              style={{
                padding: "1.5rem",
                borderBottom: "1px solid var(--game-border)",
                maxWidth: "50%",
                width: "100%",
              }}
            >
              <div style={{ display: "flex", gap: "1rem" }}>
                <div style={{ flex: 1 }}>
                  <Select
                    options={bookReportChanges.map((c) => ({
                      value: c.player_id,
                      label: c.player_name,
                    }))}
                    isSearchable
                    placeholder="Select Player"
                    className="game-select"
                    classNamePrefix="select"
                    styles={{
                      control: (base) => ({
                        ...base,
                        backgroundColor: "#2d3748",
                        borderColor: "#4a5568",
                        color: "#e2e8f0",
                      }),
                      menu: (base) => ({ ...base, backgroundColor: "#2d3748" }),
                      option: (base, { isFocused }) => ({
                        ...base,
                        backgroundColor: isFocused ? "#4a5568" : "#2d3748",
                        color: "#e2e8f0",
                      }),
                      singleValue: (base) => ({ ...base, color: "#e2e8f0" }),
                    }}
                    onChange={(opt) =>
                      setBookReportPlayerId(opt ? opt.value : "")
                    }
                    value={
                      bookReportChanges
                        .map((c) => ({
                          value: c.player_id,
                          label: c.player_name,
                        }))
                        .find((o) => o.value === bookReportPlayerId) || null
                    }
                  />
                </div>
                <button
                  className="game-btn game-btn--primary"
                  onClick={searchBookReport}
                  disabled={!bookReportPlayerId}
                >
                  Search
                </button>
              </div>
            </div>

            <div className="game-modal__body game-modal__body--scroll">
              {bookReportPlayerId && (
                <div style={{ marginBottom: "2rem" }}>
                  <h4 style={{ marginBottom: "1rem" }}>Transactions</h4>
                  {bookReportTransactions === null ? (
                    <p className="game-empty">
                      Click Search to load transactions
                    </p>
                  ) : bookReportTransactions.length === 0 ? (
                    <p className="game-empty">No transactions found.</p>
                  ) : (
                    <div className="game-transaction-list">
                      {bookReportTransactions.map((t) => {
                        const amount = parseFloat(t.amount || 0);
                        // Increase Debt (Red): 1, 8, 9, 17, 18, 30
                        // Decrease Debt (Green): 4, 7, 10, 11, 12, 13, 14, 15, 16, 31
                        const isDecrease = [
                          4, 7, 10, 11, 12, 13, 14, 15, 16, 31,
                        ].includes(t.code);
                        const title = formatTransactionCode(
                          t.code,
                          t.type,
                          t.method,
                        );

                        return (
                          <div
                            key={t.id}
                            className="game-transaction-item"
                            style={{
                              background: isDecrease
                                ? "rgba(72, 187, 120, 0.08)"
                                : "rgba(245, 101, 101, 0.08)",
                              border: `1px solid ${isDecrease ? "rgba(72, 187, 120, 0.2)" : "rgba(245, 101, 101, 0.2)"}`,
                              padding: "1rem 1.25rem",
                            }}
                          >
                            <span
                              className="game-transaction-time"
                              style={{ width: "80px" }}
                            >
                              {formatTime(t.completed_at)}
                            </span>
                            <span
                              className="game-transaction-amount"
                              style={{
                                width: "70px",
                                fontSize: "1.1rem",
                                fontWeight: 800,
                                color: isDecrease
                                  ? "var(--game-success)"
                                  : "var(--game-danger)",
                              }}
                            >
                              ${Math.abs(amount).toFixed(0)}
                            </span>

                            <div
                              style={{
                                flex: 1,
                                display: "flex",
                                alignItems: "center",
                                gap: "0.75rem",
                              }}
                            >
                              <strong style={{ fontSize: "1rem" }}>
                                {title}
                              </strong>
                              {(t.receiver_name || t.name) && (
                                <span
                                  style={{ color: "var(--game-text-muted)" }}
                                >
                                  {t.receiver_name || t.name}
                                </span>
                              )}
                            </div>

                            <span
                              className="game-transaction-date"
                              style={{
                                width: "100px",
                                textAlign: "right",
                                color: "var(--game-text-muted)",
                              }}
                            >
                              {formatDate(t.completed_at)}
                            </span>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}

              {bookReportChanges.length > 0 && (
                <div
                  style={{
                    borderTop: bookReportPlayerId
                      ? "1px solid var(--game-border)"
                      : "none",
                    pt: bookReportPlayerId ? "2rem" : "0",
                  }}
                >
                  <h4
                    style={{
                      marginBottom: "1rem",
                      marginTop: bookReportPlayerId ? "1.5rem" : "0",
                    }}
                  >
                    Book Changes For Session
                  </h4>
                  <div className="game-transaction-list">
                    {bookReportChanges.map((change) => (
                      <div
                        key={change.player_id}
                        className="game-transaction-item"
                        style={{
                          display: "flex",
                          alignItems: "center",
                          padding: "1rem",
                          justifyContent: "space-between",
                        }}
                      >
                        {/* 1. Name Section: Aligned Left */}
                        <div
                          style={{
                            flex: 1,
                            display: "flex",
                            justifyContent: "flex-start",
                          }}
                        >
                          <strong
                            style={{ cursor: "pointer" }}
                            onClick={() => {
                              setBookReportPlayerId(change.player_id);
                              searchBookReport();
                            }}
                          >
                            {change.player_name}
                          </strong>
                        </div>

                        {/* 2. Balance Section: Aligned Center */}
                        <div
                          style={{
                            flex: 2, // Gives the center more room to prevent wrapping
                            display: "flex",
                            justifyContent: "center",
                            gap: "2rem",
                            alignItems: "center",
                          }}
                        >
                          <div
                            style={{
                              display: "flex",
                              gap: "0.5rem",
                              alignItems: "center",
                            }}
                          >
                            <span
                              className="game-text-muted"
                              style={{ fontSize: "0.8rem" }}
                            >
                              Old:
                            </span>
                            <span
                              className={`game-player-badge ${
                                parseFloat(change.old_book || 0) > 0
                                  ? "game-player-badge--debt"
                                  : "game-player-badge--credit"
                              }`}
                            >
                              $
                              {Math.abs(
                                parseFloat(change.old_book || 0),
                              ).toFixed(0)}
                            </span>
                          </div>

                          <span
                            style={{
                              fontWeight: 700,
                              color: "var(--game-text-muted)",
                            }}
                          >
                            →
                          </span>

                          <div
                            style={{
                              display: "flex",
                              gap: "0.5rem",
                              alignItems: "center",
                            }}
                          >
                            <span
                              className="game-text-muted"
                              style={{ fontSize: "0.8rem" }}
                            >
                              Updated:
                            </span>
                            <span
                              className={`game-player-badge ${
                                parseFloat(change.current_book || 0) > 0
                                  ? "game-player-badge--debt"
                                  : "game-player-badge--credit"
                              }`}
                            >
                              $
                              {Math.abs(
                                parseFloat(change.current_book || 0),
                              ).toFixed(0)}
                            </span>
                          </div>
                        </div>

                        {/* 3. Spacer Section: Aligned Right (Balances the Name Section) */}
                        <div style={{ flex: 1 }} />
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </>
      )}

      {/* Wallet Report Modal */}
      {showWalletReportModal && (
        <>
          <div
            className="game-modal-overlay"
            onClick={() => {
              setShowWalletReportModal(false);
              setWalletReportSessionId(null);
            }}
          />
          <div className="game-modal game-modal--large">
            <div className="game-modal__header">
              <h3 className="game-modal__title">
                Wallet Changes -{" "}
                {formatDate(
                  sessions.find((s) => s.id === walletReportSessionId)
                    ?.start_time,
                )}
              </h3>
              <button
                className="game-icon-btn"
                onClick={() => {
                  setShowWalletReportModal(false);
                  setWalletReportSessionId(null);
                }}
              >
                <FaTimes />
              </button>
            </div>

            <div
              style={{
                padding: "1.5rem",
                borderBottom: "1px solid var(--game-border)",
                maxWidth: "50%",
                width: "100%",
              }}
            >
              <div style={{ display: "flex", gap: "1rem" }}>
                <div style={{ flex: 1 }}>
                  <Select
                    options={allWallets.map((w) => ({
                      value: w.id,
                      label: w.name,
                    }))}
                    isSearchable
                    placeholder="Select Wallet"
                    className="game-select"
                    classNamePrefix="select"
                    styles={{
                      control: (base) => ({
                        ...base,
                        backgroundColor: "#2d3748",
                        borderColor: "#4a5568",
                        color: "#e2e8f0",
                      }),
                      menu: (base) => ({ ...base, backgroundColor: "#2d3748" }),
                      option: (base, { isFocused }) => ({
                        ...base,
                        backgroundColor: isFocused ? "#4a5568" : "#2d3748",
                        color: "#e2e8f0",
                      }),
                      singleValue: (base) => ({ ...base, color: "#e2e8f0" }),
                    }}
                    onChange={(opt) =>
                      setWalletReportWalletId(opt ? opt.value : "")
                    }
                    value={
                      allWallets
                        .map((w) => ({ value: w.id, label: w.name }))
                        .find((o) => o.value === walletReportWalletId) || null
                    }
                  />
                </div>
                <button
                  className="game-btn game-btn--primary"
                  onClick={searchWalletReport}
                  disabled={!walletReportWalletId}
                >
                  Search
                </button>
              </div>
            </div>

            <div className="game-modal__body game-modal__body--scroll">
              {walletReportWalletId && (
                <div style={{ marginBottom: "2rem" }}>
                  <h4 style={{ marginBottom: "1rem" }}>Transactions</h4>
                  {walletReportTransactions === null ? (
                    <p className="game-empty">
                      Click Search to load transactions
                    </p>
                  ) : walletReportTransactions.length === 0 ? (
                    <p className="game-empty">No transactions found.</p>
                  ) : (
                    <div className="game-transaction-list">
                      {walletReportTransactions.map((t) => {
                        const amount = parseFloat(t.amount || 0);
                        const code = Number(t.code);
                        const isIncrease = WALLET_INCREASE_CODES.includes(code);
                        const isDecrease = WALLET_DECREASE_CODES.includes(code);
                        const title = formatTransactionCode(
                          t.code,
                          t.source,
                          t.method,
                        );

                        return (
                          <div
                            key={t.id}
                            className="game-transaction-item"
                            style={{
                              background: isIncrease
                                ? "rgba(72, 187, 120, 0.08)"
                                : isDecrease
                                  ? "rgba(245, 101, 101, 0.08)"
                                  : "transparent",
                              border: `1px solid ${isIncrease ? "rgba(72, 187, 120, 0.2)" : isDecrease ? "rgba(245, 101, 101, 0.2)" : "var(--game-border)"}`,
                              padding: "1rem 1.25rem",
                            }}
                          >
                            <span
                              className="game-transaction-time"
                              style={{ width: "80px" }}
                            >
                              {formatTime(t.completed_at)}
                            </span>
                            <span
                              className="game-transaction-amount"
                              style={{
                                width: "70px",
                                fontSize: "1.1rem",
                                fontWeight: 800,
                                color: isIncrease
                                  ? "var(--game-success)"
                                  : isDecrease
                                    ? "var(--game-danger)"
                                    : "var(--game-text-primary)",
                              }}
                            >
                              ${Math.abs(amount).toFixed(0)}
                            </span>

                            <div
                              style={{
                                flex: 1,
                                display: "flex",
                                alignItems: "center",
                                gap: "0.75rem",
                              }}
                            >
                              <strong style={{ fontSize: "1rem" }}>
                                {title}
                              </strong>
                              {t.entity_name && (
                                <span
                                  style={{ color: "var(--game-text-muted)" }}
                                >
                                  {t.entity_name}
                                </span>
                              )}
                            </div>

                            <span
                              className="game-transaction-date"
                              style={{
                                width: "100px",
                                textAlign: "right",
                                color: "var(--game-text-muted)",
                              }}
                            >
                              {formatDate(t.completed_at)}
                            </span>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}

              {walletReportChanges.length > 0 && (
                <div
                  style={{
                    borderTop: walletReportWalletId
                      ? "1px solid var(--game-border)"
                      : "none",
                    pt: walletReportWalletId ? "2rem" : "0",
                  }}
                >
                  <h4
                    style={{
                      marginBottom: "1rem",
                      marginTop: walletReportWalletId ? "1.5rem" : "0",
                    }}
                  >
                    Wallet Changes For Session
                  </h4>
                  <div className="game-transaction-list">
                    {walletReportChanges.map((change) => (
                      <div
                        key={change.wallet_id}
                        className="game-transaction-item"
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                          padding: "0.75rem 1rem",
                        }}
                      >
                        {/* Left-aligned Name Section */}
                        <strong
                          style={{
                            cursor: "pointer",
                            minWidth: "150px", // Fixed width keeps the center section stable
                            textAlign: "left",
                          }}
                          onClick={() => {
                            setWalletReportWalletId(change.wallet_id);
                            searchWalletReport();
                          }}
                        >
                          {change.wallet_name}
                        </strong>

                        {/* Centered Balance Section */}
                        <div
                          style={{
                            display: "flex",
                            flex: 1,
                            justifyContent: "center",
                            gap: "1.5rem",
                            alignItems: "center",
                          }}
                        >
                          <div
                            style={{
                              display: "flex",
                              gap: "0.5rem",
                              alignItems: "center",
                            }}
                          >
                            <span
                              className="game-text-muted"
                              style={{ fontSize: "0.8rem" }}
                            >
                              Old:
                            </span>
                            <span
                              className={`game-player-badge ${
                                parseFloat(change.old_balance || 0) > 0
                                  ? "game-player-badge--debt"
                                  : "game-player-badge--credit"
                              }`}
                            >
                              $
                              {Math.abs(
                                parseFloat(change.old_balance || 0),
                              ).toFixed(0)}
                            </span>
                          </div>

                          <span
                            style={{
                              fontWeight: 700,
                              color: "var(--game-text-muted)",
                            }}
                          >
                            →
                          </span>

                          <div
                            style={{
                              display: "flex",
                              gap: "0.5rem",
                              alignItems: "center",
                            }}
                          >
                            <span
                              className="game-text-muted"
                              style={{ fontSize: "0.8rem" }}
                            >
                              Updated:
                            </span>
                            <span
                              className={`game-player-badge ${
                                parseFloat(change.current_balance || 0) > 0
                                  ? "game-player-badge--debt"
                                  : "game-player-badge--credit"
                              }`}
                            >
                              $
                              {Math.abs(
                                parseFloat(change.current_balance || 0),
                              ).toFixed(0)}
                            </span>
                          </div>
                        </div>

                        {/* Invisible Spacer to balance the name's minWidth and ensure true centering */}
                        <div style={{ minWidth: "150px" }} />
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </>
      )}

      {/* Session Payouts Modal */}
      {selectedSessionId !== null && (
        <>
          <div
            className="game-modal-overlay"
            onClick={() => {
              setSelectedSessionId(null);
            }}
          />
          <div className="game-modal game-modal--large">
            <div className="game-modal__header">
              <h3 className="game-modal__title">
                Session Payouts -{" "}
                {formatDate(
                  sessions.find((s) => s.id === selectedSessionId)?.start_time,
                )}
              </h3>
              <button
                className="game-icon-btn"
                onClick={() => {
                  setSelectedSessionId(null);
                }}
              >
                <FaTimes />
              </button>
            </div>

            <div
              className="game-modal__filters"
              style={{
                display: "flex",
                alignItems: "center",
                gap: "1rem",
                flexWrap: "wrap",
              }}
            >
              <div style={{ display: "flex", gap: "0.5rem" }}>
                {[
                  { key: "staff", label: "Staff" },
                  { key: "costs", label: "Costs" },
                  { key: "wallets", label: "Wallets" },
                ].map(({ key, label }) => (
                  <label key={key} className="game-filter-chip">
                    <input
                      type="checkbox"
                      checked={sessionPayoutFilters[key]}
                      onChange={(e) =>
                        setSessionPayoutFilters((prev) => ({
                          ...prev,
                          [key]: e.target.checked,
                        }))
                      }
                    />
                    <span>{label}</span>
                  </label>
                ))}
              </div>
              <div
                style={{ display: "flex", gap: "0.5rem", marginLeft: "auto" }}
              >
                <button
                  className="game-btn game-btn--sm game-btn--secondary"
                  style={{ minWidth: "60px", justifyContent: "center" }}
                  onClick={() =>
                    setSessionPayoutFilters({
                      staff: true,
                      costs: true,
                      wallets: true,
                    })
                  }
                >
                  All
                </button>
                <button
                  className="game-btn game-btn--sm game-btn--secondary"
                  style={{ minWidth: "60px", justifyContent: "center" }}
                  onClick={() =>
                    setSessionPayoutFilters({
                      staff: false,
                      costs: false,
                      wallets: false,
                    })
                  }
                >
                  None
                </button>
              </div>
            </div>

            <div className="game-modal__body game-modal__body--scroll">
              {!sessionTransactions[selectedSessionId] ? (
                <p className="game-empty">Loading...</p>
              ) : (
                <div className="game-transaction-list">
                  {sessionTransactions[selectedSessionId]
                    .filter((t) => {
                      const code = Number(t.code);
                      if ([24, 26, 27, 28, 29].includes(code))
                        return sessionPayoutFilters.staff;
                      if (code === 32) return sessionPayoutFilters.costs;
                      if (code === 36 || t.wallet_id)
                        return sessionPayoutFilters.wallets;
                      return true;
                    })
                    .map((txn) => {
                      const amt = parseFloat(txn.amount || 0);
                      const code = Number(txn.code);
                      const title = formatTransactionCode(
                        txn.code,
                        txn.type,
                        txn.method,
                      );

                      return (
                        <div
                          key={txn.id}
                          className="game-transaction-item"
                          style={{
                            background: "transparent",
                            border: `1px solid var(--game-border)`,
                            padding: "1rem 1.25rem",
                          }}
                        >
                          <span
                            className="game-transaction-time"
                            style={{ width: "80px" }}
                          >
                            {formatTime(txn.completed_at || txn.created_at)}
                          </span>
                          <span
                            className="game-transaction-amount"
                            style={{
                              width: "70px",
                              fontSize: "1.1rem",
                              fontWeight: 800,
                              color: "var(--game-text-primary)",
                            }}
                          >
                            ${Math.abs(amt).toFixed(0)}
                          </span>

                          <div
                            style={{
                              flex: 1,
                              display: "flex",
                              alignItems: "center",
                              gap: "0.75rem",
                            }}
                          >
                            <strong style={{ fontSize: "1rem" }}>
                              {title}
                            </strong>
                            {txn.name && (
                              <span style={{ color: "var(--game-text-muted)" }}>
                                {[36].includes(code) || txn.wallet_id
                                  ? amt > 0
                                    ? " to "
                                    : " from "
                                  : ""}
                                {txn.name}
                              </span>
                            )}
                            {txn.description && (
                              <span
                                style={{
                                  fontStyle: "italic",
                                  color: "var(--game-text-muted)",
                                  fontSize: "0.85rem",
                                }}
                              >
                                {txn.description}
                              </span>
                            )}
                          </div>

                          <span
                            className="game-transaction-date"
                            style={{
                              width: "100px",
                              textAlign: "right",
                              color: "var(--game-text-muted)",
                            }}
                          >
                            {formatDate(txn.completed_at || txn.created_at)}
                          </span>
                        </div>
                      );
                    })}
                </div>
              )}
            </div>
          </div>
        </>
      )}

      {/* Session Cash Transactions Modal */}
      {selectedSessionCashId !== null && (
        <>
          <div
            className="game-modal-overlay"
            onClick={() => {
              setSelectedSessionCashId(null);
            }}
          />
          <div className="game-modal game-modal--large">
            <div className="game-modal__header">
              <h3 className="game-modal__title">
                Cash Transactions -{" "}
                {formatDate(
                  sessions.find((s) => s.id === selectedSessionCashId)
                    ?.start_time,
                )}
              </h3>
              <button
                className="game-icon-btn"
                onClick={() => {
                  setSelectedSessionCashId(null);
                }}
              >
                <FaTimes />
              </button>
            </div>

            <div className="game-modal__body game-modal__body--scroll">
              {!sessionCashTransactions[selectedSessionCashId] ? (
                <p className="game-empty">Loading...</p>
              ) : sessionCashTransactions[selectedSessionCashId].length ===
                0 ? (
                <p className="game-empty">No transactions.</p>
              ) : (
                <div className="game-transaction-list">
                  {sessionCashTransactions[selectedSessionCashId].map((t) => {
                    const isVerify = t.type === "verify";
                    const isRefill = t.type === "refill";
                    const isClear = t.type === "clear";
                    const amt = parseFloat(t.amount);
                    return (
                      <div key={t.id} className="game-transaction-item">
                        <span className="game-transaction-time">
                          {formatTime(t.completed_at)}
                        </span>
                        <strong className="game-transaction-player">
                          {t.player_name || ""}
                        </strong>
                        <span
                          className={`game-transaction-amount ${amt < 0 ? "game-text-danger" : "game-text-success"}`}
                        >
                          ${Math.abs(amt).toFixed(0)}
                        </span>
                        {isVerify ? (
                          <span className="game-badge game-badge--credit">
                            verification
                          </span>
                        ) : isRefill ? (
                          <span className="game-badge game-badge--running">
                            refill
                          </span>
                        ) : isClear ? (
                          <span className="game-badge game-badge--debt">
                            cleared
                          </span>
                        ) : (
                          <>
                            <span className="game-transaction-type">
                              {formatTransactionCode(t.code, t.type, t.method)}
                            </span>
                            {t.table_name && (
                              <span className="game-transaction-table">
                                {t.table_name}
                              </span>
                            )}
                          </>
                        )}
                        <span className="game-transaction-date">
                          {formatDate(t.completed_at)}
                        </span>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </>
      )}

      {/* Bonus Modal */}
      {showBonusModal && (
        <>
          <div
            className="game-modal-overlay"
            onClick={() => {
              setShowBonusModal(false);
              setBonusSessionId(null);
            }}
          />
          <div className="game-modal game-modal--large">
            <div className="game-modal__header">
              <h3 className="game-modal__title">
                Bonuses -{" "}
                {formatDate(
                  sessions.find((s) => s.id === bonusSessionId)?.start_time,
                )}
              </h3>
              <button
                className="game-icon-btn"
                onClick={() => {
                  setShowBonusModal(false);
                  setBonusSessionId(null);
                }}
              >
                <FaTimes />
              </button>
            </div>

            <div className="game-modal__body game-modal__body--scroll">
              {bonusTransactions.length === 0 ? (
                <p className="game-empty">No bonuses.</p>
              ) : (
                <div className="game-transaction-list">
                  {bonusTransactions.map((bonus) => (
                    <div key={bonus.id} className="game-transaction-item">
                      <span className="game-transaction-time">
                        {formatTime(bonus.completed_at)}
                      </span>
                      <strong className="game-transaction-player">
                        {bonus.player_name}
                      </strong>
                      <span className="game-transaction-amount">
                        ${parseFloat(bonus.amount).toFixed(0)}
                      </span>
                      <span className="game-transaction-type">
                        {bonus.book === 1 ? "Book" : "Deposit"}
                      </span>
                      {bonus.table_name && (
                        <span className="game-transaction-table">
                          {bonus.table_name}
                        </span>
                      )}
                      <span className="game-transaction-date">
                        {formatDate(bonus.completed_at)}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </>
      )}

      {showHistoryModal && (
        <>
          <div
            className="game-modal-overlay"
            onClick={() => {
              setShowHistoryModal(false);
              setHistoryResults([]);
              setHistoryOffset(0);
              setHasMoreHistory(false);
            }}
          />
          <div className="game-modal game-modal--large">
            <div className="game-modal__header">
              <h3
                className="game-modal__title"
                style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}
              >
                {historySearchEntity?.label}'s Transaction History
                {historyResults.length > 0 && (
                  <FaFileCsv
                    className="game-icon-btn game-icon-btn--success"
                    onClick={(e) => {
                      const data = getFilteredHistoryResults().map((tx) => ({
                        "Date/Time": `${formatTime(tx.completed_at)} ${formatDate(tx.completed_at)}`,
                        Action: formatTransactionCode(tx.code),
                        Amount: tx.amount,
                        Method: tx.method || "",
                      }));
                      exportToCSV(
                        `${historySearchEntity?.label}_Transactions`,
                        Object.keys(data[0] || {}),
                        data,
                        e,
                      );
                    }}
                  />
                )}
              </h3>
              <button
                className="game-icon-btn"
                onClick={() => {
                  setShowHistoryModal(false);
                  setHistoryResults([]);
                  setHistoryOffset(0);
                  setHasMoreHistory(false);
                }}
              >
                <FaTimes />
              </button>
            </div>

            {(historySearchType === "player" ||
              historySearchType === "dealer") && (
              <div className="game-modal__filters">
                {historySearchType === "player" ? (
                  <>
                    {[
                      { key: "deposit", label: "Deposit" },
                      { key: "withdrawal", label: "Withdrawal" },
                      { key: "book", label: "Book" },
                    ].map(({ key, label }) => (
                      <label key={key} className="game-filter-chip">
                        <input
                          type="checkbox"
                          checked={historyTxFilters[key]}
                          onChange={(e) =>
                            setHistoryTxFilters((prev) => ({
                              ...prev,
                              [key]: e.target.checked,
                            }))
                          }
                        />
                        <span>{label}</span>
                      </label>
                    ))}
                    <div className="game-filter-actions">
                      <button
                        type="button"
                        className="game-btn game-btn--sm"
                        onClick={() =>
                          setHistoryTxFilters((prev) => ({
                            ...prev,
                            deposit: true,
                            withdrawal: true,
                            book: true,
                          }))
                        }
                      >
                        All
                      </button>
                      <button
                        type="button"
                        className="game-btn game-btn--sm"
                        onClick={() =>
                          setHistoryTxFilters((prev) => ({
                            ...prev,
                            deposit: false,
                            withdrawal: false,
                            book: false,
                          }))
                        }
                      >
                        None
                      </button>
                    </div>
                  </>
                ) : (
                  <>
                    {[
                      { key: "rake", label: "Rake" },
                      { key: "tips", label: "Tips" },
                      { key: "payout", label: "Payout" },
                    ].map(({ key, label }) => (
                      <label key={key} className="game-filter-chip">
                        <input
                          type="checkbox"
                          checked={historyTxFilters[key]}
                          onChange={(e) =>
                            setHistoryTxFilters((prev) => ({
                              ...prev,
                              [key]: e.target.checked,
                            }))
                          }
                        />
                        <span>{label}</span>
                      </label>
                    ))}
                    <div className="game-filter-actions">
                      <button
                        type="button"
                        className="game-btn game-btn--sm"
                        onClick={() =>
                          setHistoryTxFilters((prev) => ({
                            ...prev,
                            rake: true,
                            tips: true,
                            payout: true,
                          }))
                        }
                      >
                        All
                      </button>
                      <button
                        type="button"
                        className="game-btn game-btn--sm"
                        onClick={() =>
                          setHistoryTxFilters((prev) => ({
                            ...prev,
                            rake: false,
                            tips: false,
                            payout: false,
                          }))
                        }
                      >
                        None
                      </button>
                    </div>
                  </>
                )}
              </div>
            )}

            <div className="game-modal__body game-modal__body--scroll">
              {(() => {
                const filteredResults = getFilteredHistoryResults();

                if (filteredResults.length === 0) {
                  return historyResults.length > 0 ? (
                    <p className="game-empty">
                      No matching transactions (try adjusting filters).
                    </p>
                  ) : (
                    !isHistoryLoading && (
                      <p className="game-empty">No transactions found.</p>
                    )
                  );
                }

                // Reset session color logic for filtered set
                let lastSessionId = null;
                let currentColorIndex = 1; // Start with 1 so first new session becomes 0

                return (
                  <div style={{ overflowX: "auto" }}>
                    {(filteredResults.some((t) => t.player_book !== 0) ||
                      filteredResults.some((t) => t.player_credit !== 0)) && (
                      <div
                        style={{
                          overflowX: "auto",
                          display: "flex",
                          gap: "6px",
                          alignItems: "center",
                          flexWrap: "wrap",
                          marginBottom: "10px",
                        }}
                      >
                        {filteredResults.some(
                          (t) =>
                            t.player_credit != 0 && t.player_credit != null,
                        ) && (
                          <span className="game-player-badge game-player-badge--session-yellow">
                            Session Credit
                          </span>
                        )}

                        {filteredResults.some((t) => t.player_book > 0) && (
                          <span className="game-player-badge game-player-badge--debt">
                            Account Debt
                          </span>
                        )}

                        {filteredResults.some((t) => t.player_book < 0) && (
                          <span className="game-player-badge game-player-badge--credit">
                            Player Balance
                          </span>
                        )}
                      </div>
                    )}
                    <table
                      style={{
                        width: "100%",
                        borderCollapse: "collapse",
                        fontSize: "0.875rem",
                      }}
                    >
                      <thead>
                        <tr
                          style={{
                            borderBottom: "2px solid var(--game-border)",
                            textAlign: "left",
                          }}
                        >
                          <th style={{ padding: "0.75rem" }}>Time</th>
                          <th style={{ padding: "0.75rem" }}>Action</th>
                          <th style={{ padding: "0.75rem" }}>Amount</th>
                          <th style={{ padding: "0.75rem" }}>Method</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredResults.map((tx, idx) => {
                          const isNewSession = tx.session_id !== lastSessionId;
                          if (isNewSession) {
                            currentColorIndex = (currentColorIndex + 1) % 2;
                            lastSessionId = tx.session_id;
                          }

                          const bg =
                            currentColorIndex === 1
                              ? "rgba(49, 130, 206, 0.15)" // Blue session block
                              : "rgba(236, 201, 75, 0.1)"; // Yellow session block

                          return (
                            <tr
                              key={`${tx.source}-${tx.id}`}
                              style={{
                                borderBottom: "1px solid var(--game-border)",
                                background: bg,
                              }}
                            >
                              <td
                                style={{
                                  padding: "0.75rem",
                                  whiteSpace: "nowrap",
                                }}
                              >
                                <div style={{ fontWeight: 500 }}>
                                  {formatTime(tx.completed_at)},{" "}
                                  {formatDate(tx.completed_at)}
                                </div>
                              </td>
                              <td style={{ padding: "0.75rem" }}>
                                {formatTransactionCode(tx.code)}
                              </td>
                              <td style={{ padding: "0.75rem" }}>
                                <div
                                  className="game-player-badges-container"
                                  style={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: "4px",
                                    marginTop: "4px",
                                    width: "100%",
                                  }}
                                >
                                  <span style={{ fontWeight: 700 }}>
                                    {formatCurrency(tx.amount)}
                                  </span>

                                  <div
                                    style={{
                                      display: "flex",
                                      gap: "4px",
                                      marginLeft: "auto",
                                    }}
                                  >
                                    {tx.player_credit != 0 &&
                                      tx.player_credit != null && (
                                        <span className="game-player-badge game-player-badge--session-yellow">
                                          ${tx.player_credit}
                                        </span>
                                      )}

                                    {tx.player_book > 0 && (
                                      <span className="game-player-badge game-player-badge--debt">
                                        ${tx.player_book}
                                      </span>
                                    )}

                                    {tx.player_book < 0 && (
                                      <span className="game-player-badge game-player-badge--credit">
                                        ${Math.abs(tx.player_book)}
                                      </span>
                                    )}
                                  </div>
                                </div>
                              </td>
                              <td style={{ padding: "0.75rem" }}>
                                {tx.method || ""}
                                {tx.receiver_name && (
                                  <span
                                    style={{ color: "var(--game-text-muted)" }}
                                  >
                                    {(WITHDRAWAL_CODES.includes(tx.code)
                                      ? " from "
                                      : " to ") + tx.receiver_name}
                                  </span>
                                )}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                    {hasMoreHistory && (
                      <div
                        style={{
                          textAlign: "center",
                          marginTop: "1.5rem",
                          paddingBottom: "1rem",
                        }}
                      >
                        <button
                          className="game-btn game-btn--secondary"
                          onClick={() => handleHistorySearch(false)}
                          disabled={isHistoryLoading}
                        >
                          {isHistoryLoading ? "Loading..." : "Load More"}
                        </button>
                      </div>
                    )}
                  </div>
                );
              })()}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
